import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import { formatPhone } from './utils/phone';
import {
  Phone, MessageSquare, Users, Settings, BarChart3, Plus, Search, Filter,
  Download, Upload, Send, CheckCircle, Clock, XCircle, Edit2, Trash2, Eye, EyeOff,
  LogOut, Bell, FileText, TrendingUp, Menu, X, User, Save, Copy, CheckCheck,
  Zap, Play, Pause, AlertTriangle, DollarSign, Target, Activity, GitBranch,
  Calendar, ArrowRight, Layers, RefreshCw, Timer, Percent, TrendingDown,
  Award, ShieldAlert, AlertCircle, Flame, Thermometer, Snowflake, Star,
  ThumbsUp, ThumbsDown, UserCheck, PhoneCall, Mail, MessageCircle, Hash,
  Sparkles, Brain, ChevronRight, ChevronLeft, CheckSquare, Repeat, Link, Unlink, Bot, Shield, LayoutDashboard
} from './components/icons/HeroshhiIcons';
import { authAPI, leadsAPI, messagesAPI } from './services/api';
import config from './config';
import BrandIcon, { BRAND_ICON_NAMES } from './components/BrandIcons';   // 🎨 Heroshhi's own icons
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// ============================================
// ✅ CRITICAL COMPONENTS (Load immediately)
// These are needed for initial render
// ============================================
import LoginPage from './pages/auth/LoginPage';
import ConnectAIPage from './components/ConnectAIPage';   // ⚡ Sign in with Heroshhi
import SignupPage from './pages/auth/SignupPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ColumnCustomizer from './components/ColumnCustomizer';
import ViewSelector from './components/ViewSelector';
import ExcelFilterDropdown from './components/ExcelFilterDropdown';
import MobileLeadList from './components/MobileLeadList';
import LogCallSheet from './components/LogCallSheet';
import { whyNow, lastOutcome, interestOf, tierOf, sourceCampaign } from './components/leadInsights';

import CarouselSendPanel, { getCarouselComponent, buildCarouselCards } from './components/CarouselSendPanel';
import { forgetThisDevice } from './utils/pushDevice';
import { STAGES, STAGE_LABELS, STAGE_TONE, stageOf } from './utils/stages';

// ⏱️ "35m ago", "10h ago", then "Yesterday" / "3d ago" by calendar day
function lastActiveLabel(when) {
  const t = new Date(when), now = new Date(), ms = now - t;
  if (ms < 60e3) return 'Just now';
  if (ms < 3600e3) return `${Math.floor(ms / 60e3)}m ago`;
  if (ms < 86400e3) return `${Math.floor(ms / 3600e3)}h ago`;
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(t)) / 86400e3);
  return days <= 1 ? 'Yesterday' : `${days}d ago`;
}
// 📋 Built-in column layouts for the Leads tab (saved layouts appear after these)
const BUILTIN_LAYOUTS = [
  { _id: 'builtin:caller', builtin: true, name: 'Caller', hint: 'Why now, last outcome, follow-up, source, spender tier',
    columns: [
      { key: 'whyNow', label: 'Why now', visible: true, order: 1 },
      { key: 'lastOutcome', label: 'Last outcome', visible: true, order: 2 },
      { key: 'nextFollowUpDate', label: 'Next follow-up', visible: true, order: 3 },
      { key: 'sourceCampaign', label: 'Source and campaign', visible: true, order: 4 }
    ] },
  { _id: 'builtin:owner', builtin: true, name: 'Owner', hint: 'Status, owner, follow-up, last activity, source',
    columns: [
      { key: 'status', label: 'Stage', visible: true, order: 1 },
      { key: 'assignedTo', label: 'Owner', visible: true, order: 2 },
      { key: 'nextFollowUpDate', label: 'Next follow-up', visible: true, order: 3 },
      { key: 'lastInteractionAt', label: 'Last activity', visible: true, order: 4 },
      { key: 'sourceCampaign', label: 'Source and campaign', visible: true, order: 5 }
    ] }
];
const CALLER_ROLES = ['sales', 'salesperson', 'staff', 'user'];


// ============================================
// ✅ LAZY LOADED COMPONENTS (Load on demand)
// These load only when the user navigates to them
// ============================================


// Modals - Load when opened
const AddLeadModal = lazy(() => import('./components/AddLeadModal'));
const ViewLeadModal = lazy(() => import('./components/ViewLeadModal'));
const EditLeadModal = lazy(() => import('./components/EditLeadModal'));
const BulkMessageModal = lazy(() => import('./components/BulkMessageModal'));
const CSVImportModal = lazy(() => import('./components/CSVImportModal'));
const ImportProgressModal = lazy(() => import('./components/ImportProgressModal'));


// Dashboards - Load when tab is selected
const RevenueDashboard = lazy(() => import('./components/RevenueDashboard'));
const OwnerDashboard = lazy(() => import('./components/OwnerDashboard'));
const MessagesTab = lazy(() => import('./components/MessagesTab'));
const QuickAutomations = lazy(() => import('./components/QuickAutomations'));
const BroadcastDashboard = lazy(() => import('./components/BroadcastDashboard'));
const PlatformAdminDashboard = lazy(() => import('./components/PlatformAdminDashboard'));
const AuditLogPanel = lazy(() => import('./components/AuditLogPanel'));

// Reports - Load when reports tab is selected
const ReportsDashboard = lazy(() => import('./components/ReportsDashboard'));
const ReportBuilder = lazy(() => import('./components/ReportBuilder'));
const ReportViewer = lazy(() => import('./components/ReportViewer'));

// Settings - Load when settings tab is selected
const WhatsAppSettings = lazy(() => import('./components/settings/WhatsAppSettings'));
const ConnectedAIApps = lazy(() => import('./components/settings/ConnectedAIApps'));
const InstagramSettings = lazy(() => import('./components/settings/InstagramSettings'));
const BusinessHoursSettings = lazy(() => import('./components/settings/BusinessHoursSettings'));
const ConversionApiSettings = lazy(() => import('./components/settings/ConversionApiSettings'));
const WebhookSettings = lazy(() => import('./components/settings/WebhookSettings'));
const GoogleSheetsSettings = lazy(() => import('./components/settings/GoogleSheetsSettings'));
const StoreSettings = lazy(() => import('./components/settings/StoreSettings'));
const WebChatSettings = lazy(() => import('./components/settings/WebChatSettings'));
const UserManagement = lazy(() => import('./components/settings/UserManagement'));
const ChatbotSettings = lazy(() => import('./components/settings/ChatbotSettings'));
const AssignmentRules = lazy(() => import('./components/settings/AssignmentRules'));
const MessageRouting = lazy(() => import('./components/settings/MessageRouting'));
const TemplateLibrary = lazy(() => import('./components/settings/TemplateLibrary'));
const TwoFactorSettings = lazy(() => import('./components/TwoFactorSettings'));

// Support - Load when needed
const SupportChatWidget = lazy(() => import('./components/SupportChatWidget'));
const SupportAdminPanel = lazy(() => import('./components/SupportAdminPanel'));

// Workflows - Load when needed
const WorkflowsTab = lazy(() => import('./components/WorkflowsTab'));
const ContactsTab = lazy(() => import('./components/ContactsTab'));
const AppointmentsTab = lazy(() => import('./components/AppointmentsTab'));
const OrdersTab = lazy(() => import('./components/OrdersTab'));
const WebChatsTab = lazy(() => import('./components/WebChatsTab'));
const PwaPrompt = lazy(() => import('./components/PwaPrompt'));
const CallQueue = lazy(() => import('./components/CallQueue'));   // 📞 Call next lead (phones, reps)
const FeesTab = lazy(() => import('./components/FeesTab'));

// ============================================
// LOADING SKELETON COMPONENT
// Shows professional loading state
// ============================================
const LoadingSkeleton = ({ type = 'table' }) => {
  if (type === 'cards') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white rounded-xl shadow p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/3"></div>
          </div>
        ))}
      </div>
    );
  }

  if (type === 'table') {
    return (
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="h-4 bg-gray-200 rounded w-1/4 animate-pulse"></div>
        </div>
        <div className="divide-y divide-gray-200">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className="px-6 py-4 flex items-center gap-4 animate-pulse">
              <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/4"></div>
              </div>
              <div className="h-6 bg-gray-200 rounded w-16"></div>
              <div className="h-6 bg-gray-200 rounded w-20"></div>
              <div className="h-8 bg-gray-200 rounded w-24"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

// ============================================
// ✅ LAZY LOADING FALLBACK COMPONENT
// Shows while lazy components are loading
// ============================================
const LazyLoadFallback = ({ type = 'default' }) => {
  if (type === 'modal') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (type === 'tab') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-10 h-10 text-blue-600 animate-spin" />
          <p className="text-gray-500">Loading content...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex items-center justify-center p-8">
      <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
    </div>
  );
};

// ============================================
// DEFAULT COLUMNS CONFIGURATION
// ============================================
const DEFAULT_COLUMNS = [
  { key: 'health', label: 'Activity', visible: true, order: 1 },   // how recently the lead engaged (the stage is separate)
  { key: 'leadType', label: 'Lead Type', visible: true, order: 2 },
  { key: 'assignedTo', label: 'Assigned To', visible: true, order: 3 },
  { key: 'segment', label: 'Segment', visible: true, order: 4 },
  { key: 'callStatus', label: 'Call Status', visible: true, order: 5 },
  { key: 'stage', label: 'Stage', visible: true, order: 6 },
  { key: 'createdAt', label: 'Created Date', visible: true, order: 7 },
  { key: 'campaignName', label: 'Campaign Name', visible: false, order: 8 },
  { key: 'adGroupName', label: 'Ad Group', visible: false, order: 9 },
  { key: 'adName', label: 'Ad Name', visible: false, order: 10 },
  { key: 'source', label: 'Source', visible: false, order: 11 },
  { key: 'city', label: 'City', visible: false, order: 12 },
  { key: 'email', label: 'Email', visible: false, order: 13 },
  { key: 'tags', label: 'Tags', visible: false, order: 14 },
  { key: 'lastContactedAt', label: 'Last Contacted', visible: true, order: 16 },
  { key: 'lastInteractionAt', label: 'Last Active', visible: true, order: 17 },
  { key: 'financial.spenderCategory', label: 'Spender Tier', visible: true, order: 18 },
];


// ============================================
// Windows moved out of the main app (it rebuilt them on every update, losing what was typed)
// ============================================
  const GoogleSheetsModal = ({ onClose, onConnect }) => {
    const [spreadsheetId, setSpreadsheetId] = useState('');
    const [sheetName, setSheetName] = useState('Sheet1');
    const [apiKey, setApiKey] = useState('');
    const [autoSync, setAutoSync] = useState(true);
    const [connecting, setConnecting] = useState(false);
    const [error, setError] = useState('');

    const handleConnect = async (e) => {
      e.preventDefault();
      setConnecting(true);
      setError('');
      try {
        const response = await fetch(`${config.API_URL}/api/sheets/connect`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ spreadsheetId, sheetName, apiKey, autoSync })
        });
        const data = await response.json();
        if (data.success) {
          onConnect();
          onClose();
        } else {
          setError(data.message);
        }
      } catch (err) {
        setError('Failed to connect: ' + err.message);
      } finally {
        setConnecting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">Connect Google Sheets</h3>
            <button onClick={onClose}>
              <X size={24} className="text-gray-400 hover:text-gray-600" />
            </button>
          </div>

          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Setup Instructions:</h4>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Open your Google Sheet</li>
              <li>Click "Share" and set to "Anyone with link can view"</li>
              <li>Copy the Spreadsheet ID from the URL</li>
              <li>Get API key from Google Cloud Console</li>
            </ol>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleConnect} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Spreadsheet ID *</label>
              <input
                type="text"
                value={spreadsheetId}
                onChange={(e) => setSpreadsheetId(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                From the URL: docs.google.com/spreadsheets/d/[SPREADSHEET_ID]/edit
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sheet Name</label>
              <input
                type="text"
                value={sheetName}
                onChange={(e) => setSheetName(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="Sheet1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Google API Key *</label>
              <input
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="AIzaSy..."
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Get from: console.cloud.google.com/apis/credentials
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="autoSync"
                checked={autoSync}
                onChange={(e) => setAutoSync(e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="autoSync" className="text-sm text-gray-700">Auto-sync every hour</label>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="hs-btn hs-btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={connecting}
                className="hs-btn hs-btn-primary flex-1"
              >
                {connecting ? 'Connecting...' : 'Connect'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const CreateDripModal = ({ onClose, onCreate }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [steps, setSteps] = useState([
      { delay: 0, templateName: '', templateLanguage: 'en' },
      { delay: 24, templateName: '', templateLanguage: 'en' },
      { delay: 72, templateName: '', templateLanguage: 'en' }
    ]);
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
      loadTemplates();
    }, []);

    const loadTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const response = await fetch(`${config.API_URL}/api/whatsapp/templates`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await response.json();
        if (data.success) {
          setTemplates(data.data || []);
        } else {
          setError(data.error || data.message || 'Could not load templates');
          console.error('Template load failed:', data);
        }
      } catch (err) {
        console.error('Load templates error:', err);
      } finally {
        setTemplatesLoading(false);
      }
    };

    const handleAddStep = () => {
      const lastDelay = steps[steps.length - 1]?.delay || 0;
      setSteps([...steps, { delay: lastDelay + 24, templateName: '', templateLanguage: 'en' }]);
    };

    const handleRemoveStep = (index) => {
      setSteps(steps.filter((_, i) => i !== index));
    };

    const handleStepChange = (index, field, value) => {
      const newSteps = [...steps];
      newSteps[index][field] = value;

      // If selecting a template, also set its language
      if (field === 'templateName') {
        const template = templates.find(t => t.name === value);
        if (template) {
          newSteps[index].templateLanguage = template.language || 'en';
        }
      }

      setSteps(newSteps);
    };

    const handleCreate = async (e) => {
      e.preventDefault();
      setCreating(true);
      setError('');

      try {
        const response = await fetch(`${config.API_URL}/api/drips`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ name, description, steps })
        });

        const data = await response.json();
        if (data.success) {
          onCreate();
          onClose();
        } else {
          setError(data.message);
        }
      } catch (err) {
        setError('Failed to create: ' + err.message);
      } finally {
        setCreating(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">Create Drip Campaign</h3>
            <button onClick={onClose}>
              <X size={24} className="text-gray-400 hover:text-gray-600" />
            </button>
          </div>

          {templatesLoading && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
              Loading templates...
            </div>
          )}

          {templates.length === 0 && !templatesLoading && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
              No approved templates found. Create templates in Meta Business Manager first.
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="Welcome Sequence"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="3-day onboarding sequence"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Message Steps (Using Templates) *
                </label>
                <button
                  type="button"
                  onClick={handleAddStep}
                  className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
                  disabled={templates.length === 0}
                >
                  + Add Step
                </button>
              </div>

              <div className="space-y-3">
                {steps.map((step, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-center mb-3">
                      <span className="font-medium text-sm">Step {index + 1}</span>
                      {steps.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveStep(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Delay (hours after enrollment)
                        </label>
                        <input
                          type="number"
                          value={step.delay}
                          onChange={(e) => handleStepChange(index, 'delay', parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border rounded text-sm"
                          min="0"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          0 = immediately, 24 = 1 day, 48 = 2 days
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-1">WhatsApp Template *</label>
                        <select
                          value={step.templateName}
                          onChange={(e) => handleStepChange(index, 'templateName', e.target.value)}
                          className="w-full px-3 py-2 border rounded text-sm"
                          required
                        >
                          <option value="">Select approved template...</option>
                          {templates.map(t => (
                            <option key={t.name} value={t.name}>
                              {t.name} ({t.language})
                            </option>
                          ))}
                        </select>
                        {step.templateName && (
                          <p className="text-xs text-green-600 mt-1">Template: {step.templateName}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-yellow-800">
                <strong>Safety:</strong> Drips use approved templates only. This prevents WhatsApp from banning your account.
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="hs-btn hs-btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || templates.length === 0}
                className="hs-btn hs-btn-primary flex-1"
              >
                {creating ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const SendMessageModal = ({ lead, onClose, onSent, whatsappStatus = {} }) => {
    // ⚡ the customer wrote in the last 24 hours → free reply window: start in Text mode
    const [messageType, setMessageType] = useState(() => {
      const t = lead && (lead.lastInboundAt || lead.lastReplyAt);
      return t && Date.now() - new Date(t).getTime() < 24 * 3600 * 1000 ? 'text' : 'template';
    });
    // ⚡ Escape closes this window
    useEffect(() => {
      const k = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
      window.addEventListener('keydown', k);
      return () => window.removeEventListener('keydown', k);
    }, [onClose]);
    const [message, setMessage] = useState('');
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateParams, setTemplateParams] = useState({});
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [carouselCards, setCarouselCards] = useState([]); // 🎠 media attached per card

    useEffect(() => {
      loadTemplates();
    }, []);

    const loadTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const response = await fetch(`${config.API_URL}/api/whatsapp/templates`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await response.json();
        if (data.success) {
          setTemplates(data.data || []);
          if (data.data?.length > 0) {
            selectTemplate(data.data[0]);
          }
        } else {
          setError(data.error || data.message || 'Failed to load templates');
          console.error('Template load failed:', data);
        }
      } catch (err) {
        setError('Failed to load templates');
      } finally {
        setTemplatesLoading(false);
      }
    };

    const selectTemplate = (template) => {
      setSelectedTemplate(template);
      const params = {};

      // 1. Check for HEADER Media requirements
      const headerComponent = template.components?.find(c => c.type === 'HEADER');
      if (headerComponent && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerComponent.format)) {
        params.headerMediaUrl = ''; // Initialize empty URL field for the header
      }

      // 2. Check for BODY variables
      const bodyComponent = template.components?.find(c => c.type === 'BODY');
      if (bodyComponent) {
        const numberedMatches = bodyComponent.text.match(/\{\{(\d+)\}\}/g) || [];
        const namedMatches = bodyComponent.text.match(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g) || [];
        
        if (numberedMatches.length > 0) {
          // Handle numbered variables: {{1}}, {{2}}, etc.
          // ✅ FIX: Smart auto-fill based on common template patterns
          numberedMatches.forEach((_, index) => {
            let defaultValue = '';
            
            // Common pattern for clinic templates:
            // {{1}} = Customer name
            // {{2}} = Business name
            // {{3}} = Date (if appointment template)
            // {{4}} = Time (if appointment template)
            
            if (index === 0) {
              // First parameter is usually customer name
              defaultValue = lead.name || '';
            } else if (index === 1) {
              // Second parameter is usually business name
              defaultValue = 'Skinette Clinic';
            } else if (index === 2) {
              // Third parameter - check if appointment exists for date
              if (lead.appointment?.date) {
                const apptDate = new Date(lead.appointment.date);
                defaultValue = apptDate.toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'numeric',
                  year: 'numeric'
                });
              }
            } else if (index === 3) {
              // Fourth parameter - check if appointment exists for time
              if (lead.appointment?.date) {
                const apptDate = new Date(lead.appointment.date);
                defaultValue = apptDate.toLocaleTimeString('en-IN', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                });
              }
            }
            
            params[`param${index + 1}`] = defaultValue;
          });
        } else if (namedMatches.length > 0) {
          // Handle named variables: {{customer_name}}, {{business_name}}, etc.
          namedMatches.forEach((match, index) => {
            const varName = match.replace(/\{\{|\}\}/g, ''); // Extract variable name
            
            // ✅ Auto-fill with lead data based on variable name
            let defaultValue = '';
            const varNameLower = varName.toLowerCase();
            
            // ✅ FIX: Check BUSINESS/COMPANY first (before generic 'name')
            // because 'business_name' contains 'name' and would match wrong condition
            if (varNameLower.includes('business') || varNameLower.includes('company') || varNameLower.includes('clinic')) {
              defaultValue = 'Skinette Clinic'; // Your business name
            } else if (varNameLower.includes('customer') || varNameLower === 'name' || (varNameLower.includes('name') && !varNameLower.includes('business'))) {
              defaultValue = lead.name || '';
            } else if (varNameLower.includes('phone')) {
              defaultValue = lead.phone || '';
            } else if (varNameLower.includes('email')) {
              defaultValue = lead.email || '';
            } else if (varNameLower.includes('appointment_date') || (varNameLower === 'date' && !varNameLower.includes('time'))) {
              // ✅ FIX: Get appointment date from lead.appointment.date (ISO string)
              if (lead.appointment?.date) {
                const apptDate = new Date(lead.appointment.date);
                defaultValue = apptDate.toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'numeric', 
                  year: 'numeric'
                });
              } else {
                defaultValue = new Date().toLocaleDateString('en-IN');
              }
            } else if (varNameLower.includes('appointment_time') || varNameLower === 'time') {
              // ✅ FIX: Extract time from lead.appointment.date (time is embedded in date!)
              if (lead.appointment?.date) {
                const apptDate = new Date(lead.appointment.date);
                defaultValue = apptDate.toLocaleTimeString('en-IN', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: true 
                });
              } else {
                defaultValue = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
              }
            }
            
            params[`param${index + 1}`] = defaultValue;
          });
        }
        
        setTemplateParams(params);
      } else {
        setTemplateParams({});
      }
    };

    const getTemplatePreview = () => {
      if (!selectedTemplate) return '';
      const bodyComponent = selectedTemplate.components?.find(c => c.type === 'BODY');
      if (!bodyComponent) return '';

      let preview = bodyComponent.text;
      
      // ✅ FIX: Replace BOTH numbered AND named variables
      const namedMatches = preview.match(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g) || [];
      const numberedMatches = preview.match(/\{\{(\d+)\}\}/g) || [];
      
      if (namedMatches.length > 0) {
        // Replace named variables in order with params
        namedMatches.forEach((match, index) => {
          const paramKey = `param${index + 1}`;
          preview = preview.replace(match, templateParams[paramKey] || match);
        });
      } else {
        // Replace numbered variables
        Object.keys(templateParams).forEach((key, index) => {
          const paramNum = index + 1;
          preview = preview.replace(`{{${paramNum}}}`, templateParams[key] || `{{${paramNum}}}`);
        });
      }
      
      return preview;
    };

    const handleSend = async (e) => {
      e.preventDefault();
      setSending(true);
      setError('');
      setSuccess('');

      try {
        let payload = { leadId: lead._id };

        if (messageType === 'template') {
          if (!selectedTemplate) {
            setError('Please select a template');
            setSending(false);
            return;
          }

          // 🎠 CAROUSEL: send through /send-template with one media per card
          const carouselComp = getCarouselComponent(selectedTemplate);
          if (carouselComp) {
            const missing = (carouselComp.cards || []).findIndex((_, i) => !carouselCards[i]?.mediaId);
            if (missing !== -1) {
              setError(`Add the image or video for card ${missing + 1}`);
              return;
            }
            const variables = Object.keys(templateParams)
              .filter(k => k.startsWith('param'))
              .sort((a, b) => Number(a.slice(5)) - Number(b.slice(5)))
              .map(k => templateParams[k]);

            const r = await fetch(`${config.API_URL}/api/whatsapp/send-template`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
              body: JSON.stringify({
                leadId: lead._id,
                phone: lead.phone,
                templateName: selectedTemplate.name,
                templateLanguage: selectedTemplate.language,
                variables,
                cards: buildCarouselCards(selectedTemplate, carouselCards)
              })
            });
            const d = await r.json();
            if (!r.ok || !d.success) {
              setError(d.message || d.error || 'Could not send the carousel');
              return;
            }
            setSuccess('Carousel sent successfully! ');
            setTimeout(() => { onSent(); onClose(); }, 1500);
            return;
          }

          // Separate Header params from Body params
          const bodyParameters = Object.keys(templateParams)
            .filter(key => key.startsWith('param'))
            .map(key => ({
              type: 'text',
              text: templateParams[key]
            }));

          payload.templateName = selectedTemplate.name;
          payload.templateLanguage = selectedTemplate.language;
          payload.content = `Template: ${selectedTemplate.name}`;
          
          payload.templateComponents = [];
          
          // Inject the BODY variables
          if (bodyParameters.length > 0) {
            payload.templateComponents.push({ type: 'body', parameters: bodyParameters });
          }

          // Inject the HEADER media link if it exists
          if (templateParams.headerMediaUrl) {
            const headerFormat = selectedTemplate.components.find(c => c.type === 'HEADER').format;
            payload.templateComponents.push({
              type: 'header',
              parameters: [{
                type: headerFormat.toLowerCase(),
                [headerFormat.toLowerCase()]: { link: templateParams.headerMediaUrl }
              }]
            });
          }
        } else {
          payload.content = message;
        }

        const response = await messagesAPI.send(payload);

        if (response.data.success) {
          setSuccess('Message sent successfully! ');
          setTimeout(() => {
            onSent();
            onClose();
          }, 1500);
        }
      } catch (err) {
        const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to send message';
        setError(errorMsg);
        if (errorMsg.includes('template') || errorMsg.includes('131047')) {
          setError('First message must use an approved template. Switch to Template mode or wait 24hrs after they reply.');
        }
      } finally {
        setSending(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-xl font-bold">Send WhatsApp Message</h3>
              <p className="text-sm text-gray-600">To: {lead.name} ({formatPhone(lead.phone)})</p>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="min-w-[44px] min-h-[44px] -mr-2 flex items-center justify-center rounded-xl hover:bg-gray-100">
              <X size={24} className="text-gray-400 hover:text-gray-600" />
            </button>
          </div>

          {!whatsappStatus.isConnected && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
              WhatsApp not connected. Please connect in Settings first.
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              {success}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Message Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMessageType('template')}
                className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${messageType === 'template'
                    ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                    : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                Template
                <p className="text-xs mt-1">Use approved template</p>
              </button>
              <button
                type="button"
                onClick={() => setMessageType('text')}
                className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${messageType === 'text'
                    ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                    : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                Text
                <p className="text-xs mt-1">Free text (24hr window)</p>
              </button>
            </div>
          </div>

          <form onSubmit={handleSend} className="space-y-4">
            {messageType === 'template' ? (
              <>
                {templatesLoading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="animate-spin mx-auto text-blue-600 mb-2" size={32} />
                    <p className="text-gray-600">Loading templates...</p>
                  </div>
                ) : templates.length === 0 ? (
                  <div className="text-center py-8 bg-yellow-50 rounded-lg border border-yellow-200">
                    <AlertTriangle className="mx-auto text-yellow-600 mb-2" size={32} />
                    <p className="text-yellow-800 font-medium">No approved templates found</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Create and approve templates in Meta Business Manager
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Template *
                      </label>
                      <select
                        value={selectedTemplate?.name || ''}
                        onChange={(e) => {
                          const template = templates.find(t => t.name === e.target.value);
                          if (template) selectTemplate(template);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        {templates.map(template => (
                          <option key={template.name} value={template.name}>
                            {template.name} ({template.language})
                          </option>
                        ))}
                      </select>
                      {selectedTemplate && (
                        <p className="text-xs text-gray-500 mt-1">
                          Category: {selectedTemplate.category} | Status: {selectedTemplate.status}
                        </p>
                      )}
                    </div>

                    {selectedTemplate && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-xs text-green-800 font-medium mb-2">Preview:</p>
                        <div className="bg-white rounded-lg p-3 text-sm whitespace-pre-wrap">
                          {getTemplatePreview()}
                        </div>
                      </div>
                    )}

                    {selectedTemplate && Object.keys(templateParams).length > 0 && (
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-gray-700">Template Parameters:</p>
                        {Object.keys(templateParams).map((paramKey, index) => {
                          
                          // Handle Header Media Input differently
                          if (paramKey === 'headerMediaUrl') {
                            const headerFormat = selectedTemplate.components.find(c => c.type === 'HEADER').format;
                            return (
                              <div key={paramKey}>
                                <label className="block text-sm text-gray-600 mb-1 capitalize">
                                  {headerFormat} URL (Public Link) *
                                </label>
                                <input
                                  type="url"
                                  value={templateParams[paramKey]}
                                  onChange={(e) => setTemplateParams({...templateParams, [paramKey]: e.target.value})}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  placeholder={`https://example.com/your-${headerFormat.toLowerCase()}.jpg`}
                                  required
                                />
                              </div>
                            );
                          }

                          // Normal Body Parameters
                          const bodyComponent = selectedTemplate.components?.find(c => c.type === 'BODY');
                          const namedMatches = bodyComponent?.text?.match(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g) || [];
                          
                          // Adjust index if we have a headerMediaUrl shifting things
                          const actualIndex = templateParams.headerMediaUrl ? index - 1 : index;
                          
                          const varName = namedMatches[actualIndex] 
                            ? namedMatches[actualIndex].replace(/\{\{|\}\}/g, '').replace(/_/g, ' ')
                            : `Parameter ${actualIndex + 1}`;
                          
                          return (
                            <div key={paramKey}>
                              <label className="block text-sm text-gray-600 mb-1 capitalize">
                                {varName}
                              </label>
                              <input
                                type="text"
                                value={templateParams[paramKey]}
                                onChange={(e) => setTemplateParams({
                                  ...templateParams,
                                  [paramKey]: e.target.value
                                })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder={`Enter ${varName}`}
                                required
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {selectedTemplate && getCarouselComponent(selectedTemplate) && (
                      <CarouselSendPanel
                        template={selectedTemplate}
                        cards={carouselCards}
                        onChange={setCarouselCards}
                      />
                    )}

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs text-blue-800">
                        <strong>Tip:</strong> Templates must be approved by WhatsApp.
                        First message to a new customer must use a template.
                      </p>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                    rows="6"
                    placeholder="Type your WhatsApp message here..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">{message.length} characters</p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    <strong>Note:</strong> Free text messages only work within 24 hours after
                    the customer's last reply. For first messages, use a template.
                  </p>
                </div>
              </>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="hs-btn hs-btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={sending || !whatsappStatus.isConnected || (messageType === 'template' && templates.length === 0)}
                className="hs-btn hs-btn-primary flex-1"
              >
                {sending ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

const LAST_OUTCOME_LABEL = { 'interested,callback': 'Interested or Call back', interested: 'Interested', callback: 'Call back', follow_up: 'Follow-up call', messaged: 'Messaged on WhatsApp', no_answer: 'No answer', busy: 'Busy', connected: 'Connected', not_interested: 'Not interested', converted: 'Converted', other: 'Other', none: 'No calls yet' };   // ⚡ Last outcome filter

export default function WhatsAppDashboard() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  // Remember which tab was open, so a reload doesn't dump you on the dashboard
  const [activeTab, setActiveTab] = useState(
    () => localStorage.getItem('activeTab') || 'dashboard'
  );
  useEffect(() => { localStorage.setItem('activeTab', activeTab); }, [activeTab]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // ✅ NEW: Mobile menu state
  // ✅ Notification State
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  
  // ============================================
  // ✅ SESSION TIMEOUT - 30 minutes of inactivity
  // ============================================
  const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
  const inactivityTimerRef = useRef(null);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  
  const [showAddLead, setShowAddLead] = useState(false);
  const [showEditLead, setShowEditLead] = useState(false);
  const [showViewLead, setShowViewLead] = useState(false);
  const [showSendMessage, setShowSendMessage] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  // ✅ What's in the box right now. searchQuery (which runs the search)
  // only updates once typing pauses — one search instead of one per letter.
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);
    // 🛒 Show the Orders page only when a store is connected
  const [storeConnected, setStoreConnected] = useState(false);
  useEffect(() => {
    if (!isLoggedIn) return;
    fetch(`${config.API_URL}/api/commerce/settings`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.json())
      .then(d => { const s = d.data || {}; setStoreConnected(!!(d.success && (s.catalog?.enabled || s.website?.enabled || s.woocommerce?.enabled || s.shopify?.enabled))); })
      .catch(() => {});
  }, [isLoggedIn]);
    // 🌐 Show "Website chats" when the website assistant is switched on
  const [webChatOn, setWebChatOn] = useState(false);

  // 📱 The page itself never scrolls sideways (one too-wide box used to let the
  // whole screen drag left), and the edge "bounce" doesn't reveal empty space.
  useEffect(() => {
    const clip = (window.CSS && CSS.supports && CSS.supports('overflow-x', 'clip')) ? 'clip' : 'hidden';
    document.documentElement.style.overflowX = clip;
    document.body.style.overflowX = clip;
    document.documentElement.style.overscrollBehavior = 'none';
    document.body.style.overscrollBehavior = 'none';

    // 📱 Inputs never stick out of their panel (all pages and windows).
    // iPhones give date/time boxes a built-in minimum width; side-by-side rows
    // also refused to shrink, pushing boxes past the edge.
    if (!document.getElementById('hs-fit-inputs')) {
      const st = document.createElement('style');
      st.id = 'hs-fit-inputs';
      st.textContent = `
        input, select, textarea { max-width: 100%; min-width: 0; }
        input[type="date"], input[type="time"], input[type="datetime-local"], input[type="month"] {
          -webkit-appearance: none; appearance: none; min-height: 2.5rem; background-color: #fff;
        }
        input[type="date"]:disabled, input[type="time"]:disabled { background-color: #f3f4f6; }
        input::-webkit-date-and-time-value { text-align: left; }
        .grid > *, .flex > input, .flex > select, .flex > textarea { min-width: 0; }
      `;
      document.head.appendChild(st);
    }
  }, []);

  // 📞 Call queue: phones only, for sales reps (computers and owners unchanged)
  const [isPhone, setIsPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const on = (e) => setIsPhone(e.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, []);

  // ⚙️ Settings: one section at a time (remembered in the address: ?settings=store)
  const [settingsSection, setSettingsSection] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('settings') || 'general'; } catch (e) { return 'general'; }
  });
  const openSettingsSection = (id) => {
    setSettingsSection(id);
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('tab'); u.searchParams.set('settings', id);   // the section only: reloads keep the page you are on
      window.history.replaceState(null, '', u);
    } catch (e) { /* ignore */ }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  useEffect(() => {
    if (!isLoggedIn) return;
    fetch(`${config.API_URL}/api/webchat/settings`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.json()).then(d => setWebChatOn(!!(d.success && d.data?.enabled))).catch(() => {});
  }, [isLoggedIn]);
  
  // 📲 Opening from a notification (?tab=messages) → go to that page
  useEffect(() => {
    const go = (url) => { try {
      const q = new URL(url, window.location.origin).searchParams;
      const t = q.get('tab'); if (t) setActiveTab(t);
      // "Support replied" notification → open the support chat
      if (q.get('support') === 'open') setTimeout(() => window.dispatchEvent(new Event('heroshhi:open-support')), 300);
      // a notification's ?tab= is used once, then removed, so a later reload keeps the page you are on
      if ((q.get('tab') || q.get('support')) && new URL(url, window.location.origin).href === window.location.href) {
        const u2 = new URL(window.location.href); u2.searchParams.delete('tab'); u2.searchParams.delete('support'); window.history.replaceState(null, '', u2);
      }
    } catch (e) {} };
    go(window.location.href);
    const onOpen = (e) => go(e.detail);
    window.addEventListener('heroshhi:open-url', onOpen);
    return () => window.removeEventListener('heroshhi:open-url', onOpen);
  }, []);
  const leadsRequestRef = useRef(0); // ✅ ignore answers to old searches
  const [showImportProgress, setShowImportProgress] = useState(false);
  const [importJobId, setImportJobId] = useState(null);
  const [importTotalLeads, setImportTotalLeads] = useState(0);
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);

  // ✅ NEW: Dashboard data state (separate from leads)
  // ⚡ open instantly with the last dashboard saved on this device (this user, under 12 hours old)
  const dashCacheKey = () => { try { const u = JSON.parse(localStorage.getItem('user') || 'null'); const id = u && (u._id || u.id); return id ? 'hs_dash_' + id : null; } catch (e) { return null; } };
  const [dashboardData, setDashboardData] = useState(() => {
    try { const k = dashCacheKey(); const c = k && JSON.parse(localStorage.getItem(k) || 'null');
      return c && Date.now() - c.at < 12 * 3600 * 1000 ? c.data : null; } catch (e) { return null; }
  });
  const [dashboardLoading, setDashboardLoading] = useState(false);
  
  // ✅ NEW: Server-side pagination state
  const [serverPagination, setServerPagination] = useState({
    page: 1,
    totalFiltered: 0,
    totalPages: 1
  });

  const [filterSegment, setFilterSegment] = useState('all');
  const [filterSource, setFilterSource] = useState('all'); // ✅ Source State
  const [filterFollowUpDate, setFilterFollowUpDate] = useState('all'); // ✅ Follow-up State
  // lastInteractionAt only moves when a person does something. updatedAt
  // moves on every write — including the nightly retry job — which floated
  // untouched leads to the top.
  const [leadsSortBy, setLeadsSortBy] = useState(
    () => localStorage.getItem('leadsSortBy_v2') || 'lastInteractionAt'
  );
  const [leadsSortOrder, setLeadsSortOrder] = useState('desc');
  const [selectedLeadIds, setSelectedLeadIds] = useState([]); // ✅ NEW: Bulk Message State
  const [selectAllMatching, setSelectAllMatching] = useState(false); // ✅ NEW
  const [totalMatchingCount, setTotalMatchingCount] = useState(0);   // ✅ NEW
  const [showBulkMessage, setShowBulkMessage] = useState(false);
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false); // ✅ NEW
  const [deleting, setDeleting] = useState(false);  // ✅ ADD THIS LINE
  const [filtersExpanded, setFiltersExpanded] = useState(false); // now: the Filters panel is open
  const [filterStatus, setFilterStatus] = useState('all');         // pending / contacted / …
  const [leadsView, setLeadsView] = useState(() => { try { return localStorage.getItem('hs_leads_view') || null; } catch (e) { return null; } });
  const [viewCounts, setViewCounts] = useState(null);              // counts on the quick-view tabs
  const [logCallLead, setLogCallLead] = useState(null);            // Log call sheet
  const [logCallSecs, setLogCallSecs] = useState(undefined);
  const [leadsMoreOpen, setLeadsMoreOpen] = useState(false);       // ⋯ menu (import, export, WhatsApp a group)
  const pendingCallRef = useRef(null);                             // phone: Call tapped, ask how it went on return
  const [tableViews, setTableViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [viewsLoading, setViewsLoading] = useState(false);
  const [showColumnCustomizer, setShowColumnCustomizer] = useState(false);
  const [editingView, setEditingView] = useState(null);
  const [isCreatingNewView, setIsCreatingNewView] = useState(false);


  // ✅ ADD THESE - Report Builder State
  const [reportView, setReportView] = useState('dashboard'); // 'dashboard' | 'builder' | 'viewer'
  const [selectedReportConfig, setSelectedReportConfig] = useState(null);

  // 👇 NEW: Report Saving Logic
  const saveReport = async (reportConfig) => {
    try {
      // ✅ FIX: Removed /saved from the end of the URL
      // ✅ Editing an existing report updates it instead of making a copy
      const isEdit = !!reportConfig._id;
      const response = await fetch(`${config.API_URL}/api/reports${isEdit ? `/${reportConfig._id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(reportConfig)
      });
      
      const data = await response.json();
      if (data.success) {
        alert(isEdit ? 'Report updated!' : 'Report saved successfully!');
        setSelectedReportConfig(data.data || reportConfig); // keep the saved id, so the next Edit updates it
        setReportView('viewer');
      } else {
        alert('Failed to save report: ' + data.message);
      }
    } catch (err) {
      console.error('Error saving report:', err);
      alert('Error saving report to server');
    }
  };
  // 👆 END NEW

  // ✅ STATE FOR DYNAMIC DROPDOWNS
  const [uniqueSources, setUniqueSources] = useState([]);
  const [uniqueTags, setUniqueTags] = useState([]);
  const [uniqueDiagnosis, setUniqueDiagnosis] = useState([]);
  const [uniqueTreatments, setUniqueTreatments] = useState([]);
  const [uniquePackages, setUniquePackages] = useState([]);

  // Load all master filter options from the database on login
  useEffect(() => {
    if (!isLoggedIn) return;
    
    const loadFilterOptions = async () => {
      try {
        const response = await fetch(`${config.API_URL}/api/leads/filter-options`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await response.json();
        if (data.success) {
          setUniqueSources(data.data.sources || []);
          setUniqueTags(data.data.tags || []);
          setUniqueDiagnosis(data.data.diagnoses || []);
          setUniqueTreatments(data.data.treatments || []);
          setUniquePackages(data.data.packages || []);
        }
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    };
    
    loadFilterOptions();
  }, [isLoggedIn]);

  // Filter States
  const [filterAssignedTo, setFilterAssignedTo] = useState('all');
  const [filterCallStatus, setFilterCallStatus] = useState('all');
  const [filterLastOutcome, setFilterLastOutcome] = useState('all');   // ⚡ last call outcome (saved on each lead)
  const [filterAdmissionStage, setFilterAdmissionStage] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterDealStage, setFilterDealStage] = useState('all');
  const [filterTags, setFilterTags] = useState([]); // ✅ CHANGED: Now an array for multi-select
  const [filterDiagnosis, setFilterDiagnosis] = useState([]); // ✅ FIXED to array
  const [filterTreatmentType, setFilterTreatmentType] = useState([]); // ✅ FIXED to array
  const [filterPackageName, setFilterPackageName] = useState([]); // ✅ FIXED to array
  const [filterSpenderCategory, setFilterSpenderCategory] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [allUsers, setAllUsers] = useState([]);

  // ✅ NEW: Health & Latency Filters
  const [filterHealth, setFilterHealth] = useState('all');
  const [filterLatency, setFilterLatency] = useState('all');
  const [latencyMode, setLatencyMode] = useState('new'); // 'new' = lead age, 'active' = time since last activity

  const [user, setUser] = useState(null);

  const [whatsappStatus, setWhatsappStatus] = useState({ isConnected: false });
  const [stats, setStats] = useState({
    totalLeads: 0,
    hotLeads: 0,
    warmLeads: 0,
    coldLeads: 0,
    activeDrips: 0,
    leadsInDrips: 0,
    avgEngagement: 0,
    conversionRate: 0
  });

  const [messages, setMessages] = useState([]);
  const [messagesMeta, setMessagesMeta] = useState(null);   // ⚡ paged inbox: { total, hasMore, oldestAt }
  const [messagesLoading, setMessagesLoading] = useState(false);
    const [messageSearch, setMessageSearch] = useState('');
  // Which conversation is currently open, so we can poll just that one fast

// ============================================
// 🆕 LEAD HEALTH INDICATOR - VERSION 2.0
// Complete rewrite with proper status differentiation
// ============================================
const getLeadHealth = React.useCallback((lead) => {
  const now = Date.now();
  
  // Time constants
  const h24 = 24 * 60 * 60 * 1000;      // 24 hours
  const h48 = 48 * 60 * 60 * 1000;      // 48 hours
  const h72 = 72 * 60 * 60 * 1000;      // 72 hours
  const d3 = 3 * 24 * 60 * 60 * 1000;   // 3 days
  const d5 = 5 * 24 * 60 * 60 * 1000;   // 5 days
  const d7 = 7 * 24 * 60 * 60 * 1000;   // 7 days
  const d10 = 10 * 24 * 60 * 60 * 1000; // 10 days
  const d14 = 14 * 24 * 60 * 60 * 1000; // 14 days
  const d21 = 21 * 24 * 60 * 60 * 1000; // 21 days
  const d30 = 30 * 24 * 60 * 60 * 1000; // 30 days

  // ============================================
  // HELPER: Check if outcome indicates "ATTEMPTED but NOT connected"
  // ============================================
  const isAttemptedOutcome = (outcome) => {
    const attemptedOutcomes = ['no_answer', 'busy', 'callback'];
    return attemptedOutcomes.includes((outcome || '').toLowerCase());
  };

  // ============================================
  // HELPER: Check if outcome indicates "CONNECTED (spoke to lead)"
  // ============================================
  const isConnectedOutcome = (outcome) => {
    const connectedOutcomes = ['interested', 'converted', 'follow_up', 'connected'];
    return connectedOutcomes.includes((outcome || '').toLowerCase());
  };

  // ============================================
  // HELPER: Check if callStatus indicates "NOT connected"
  // ============================================
  const isAttemptedCallStatus = (callStatus) => {
    const status = (callStatus || '').toLowerCase();
    return status.includes('not connected') || 
           status === 'call not connected' ||
           status === 'no answer' ||
           status === 'busy';
  };

  // ============================================
  // HELPER: Check if callStatus indicates "CONNECTED"
  // ============================================
  const isConnectedCallStatus = (callStatus) => {
    const status = (callStatus || '').toLowerCase();
    return status.includes('connected') && !status.includes('not connected') ||
          status.includes('interested') ||
          status === 'appointment booked' ||
          status === 'will visit later' ||
          status === 'callback scheduled';  // ✅ NEW - Fixed with || operator
  };

  // ============================================
  // HELPER: Check for HIGH INTENT signals (→ Heating Up)
  // ============================================
  const hasHighIntentSignals = (lead, interactions) => {
    const callStatus = (lead.callStatus || '').toLowerCase();
    
    // High intent call statuses
    if (callStatus === 'appointment booked' || 
        callStatus.includes('pricing') ||
        callStatus.includes('contract') ||
        callStatus.includes('demo')) {
      return true;
    }
    
    // Check interactions for high intent outcomes
    const recentInteractions = interactions.filter(i => {
      const interactionTime = new Date(i.date || i.createdAt).getTime();
      return (now - interactionTime) < d3; // Within last 3 days
    });
    
    for (const interaction of recentInteractions) {
      const notes = (interaction.notes || '').toLowerCase();
      const outcome = (interaction.outcome || '').toLowerCase();
      
      // High intent keywords in notes
      if (notes.includes('pricing') || 
          notes.includes('quote') ||
          notes.includes('contract') ||
          notes.includes('proposal') ||
          notes.includes('demo') ||
          notes.includes('meeting scheduled') ||
          notes.includes('appointment')) {
        return true;
      }
      
      // High intent outcome
      if (outcome === 'interested' || outcome === 'converted') {
        return true;
      }
    }
    
    return false;
  };

  // ============================================
  // STEP 1: CHECK TERMINAL STATES FIRST
  // ============================================
  const callStatus = (lead.callStatus || '').toLowerCase();
  const status = (lead.status || '').toLowerCase();
  
  // ✅ CONVERTED
  const isConverted = status === 'converted' || 
                      callStatus === 'converted' || 
                      callStatus === 'admission confirmed' || 
                      callStatus === 'closed won';
  
  if (isConverted) {
    return { 
      status: 'converted', 
      label: 'Converted', 
      icon: '✅', 
      color: 'bg-green-100 text-green-800',
      tooltip: 'Lead has been converted'
    };
  }

  // ⛔ DROPPED
  const isDropped = status === 'lost' || 
                    callStatus === 'not interested' || 
                    callStatus === 'dropped' || 
                    callStatus === 'dnd' || 
                    callStatus === 'wrong number';

  if (isDropped) {
    return { 
      status: 'dropped', 
      label: 'Dropped', 
      icon: '⛔', 
      color: 'bg-gray-100 text-gray-600',
      tooltip: 'Lead marked as dropped/not interested'
    };
  }

  // ============================================
  // STEP 2: GATHER INTERACTION DATA
  // ============================================
  
  // Get WhatsApp messages for this lead
  const leadMessages = messages.filter(msg => 
    (msg.leadId?._id === lead._id || msg.leadId === lead._id)
  );
  
  const outboundMsgs = leadMessages.filter(m => m.direction === 'outbound');
  const inboundMsgs = leadMessages.filter(m => m.direction === 'inbound');
  
  const lastOutbound = outboundMsgs.sort((a, b) => 
    new Date(b.createdAt) - new Date(a.createdAt)
  )[0];
  
  const lastInbound = inboundMsgs.sort((a, b) => 
    new Date(b.createdAt) - new Date(a.createdAt)
  )[0];

  // Get call/interaction history
  const interactions = lead.interactions || [];
  const sortedInteractions = [...interactions]
    .filter(i => ['call', 'whatsapp', 'email', 'meeting', 'visit'].includes(i.type))
    .sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));
  
  const lastInteraction = sortedInteractions[0];
  
  // Lead age
  const createdAt = new Date(lead.createdAt).getTime();
  const leadAge = now - createdAt;

  // ============================================
  // STEP 3: DETERMINE CONTACT STATUS
  // ============================================
  
  // Check for ATTEMPTED contacts (tried but didn't connect)
  const hasAttemptedInteraction = sortedInteractions.some(i => isAttemptedOutcome(i.outcome));
  const hasAttemptedViaField = isAttemptedOutcome(lead.attemptOutcome);
  const hasAttemptedCallStatus = isAttemptedCallStatus(lead.callStatus);
  const wasAttempted = hasAttemptedInteraction || hasAttemptedViaField || hasAttemptedCallStatus;
  
  // Check for CONNECTED contacts (actually spoke)
  const hasConnectedInteraction = sortedInteractions.some(i => isConnectedOutcome(i.outcome));
  const hasConnectedViaField = lead.attemptOutcome === 'connected';
  const hasConnectedCallStatus = isConnectedCallStatus(lead.callStatus);
  const wasConnected = hasConnectedInteraction || hasConnectedViaField || hasConnectedCallStatus;
  
  // Check if there's ANY contact attempt (WhatsApp, interaction, etc.)
  const hasAnyContactAttempt = outboundMsgs.length > 0 || 
                               sortedInteractions.length > 0 || 
                               lead.lastContactedAt ||
                               lead.attemptCount > 0;

  // ============================================
  // STEP 4: 🆕 NEW LEAD (< 24h old, no contact)
  // ============================================
  if (leadAge < h24 && !hasAnyContactAttempt) {
    return { 
      status: 'new', 
      label: 'New', 
      icon: '🆕', 
      color: 'bg-blue-100 text-blue-800',
      tooltip: 'New lead - less than 24h old'
    };
  }

  // ============================================
  // STEP 5: ⚠️ NEEDS ATTENTION (> 24h, no contact)
  // ============================================
  if (leadAge >= h24 && !hasAnyContactAttempt) {
    const daysOld = Math.floor(leadAge / h24);
    return { 
      status: 'needs_attention', 
      label: 'Needs Attention', 
      icon: '⚠️', 
      color: 'bg-yellow-100 text-yellow-800',
      tooltip: `No contact attempt yet - ${daysOld}d old`
    };
  }

  // ============================================
  // STEP 6: 📞 CONTACT ATTEMPTED (tried but not connected)
  // Only show this if we HAVEN'T successfully connected yet
  // ============================================
  if (wasAttempted && !wasConnected) {
    const attemptCount = lead.attemptCount || sortedInteractions.filter(i => isAttemptedOutcome(i.outcome)).length;
    const lastAttemptTime = lead.lastAttemptAt ? new Date(lead.lastAttemptAt).getTime() : 
                           (lastInteraction ? new Date(lastInteraction.date || lastInteraction.createdAt).getTime() : null);
    
    // Check if too many attempts with no success → Silent
    if (attemptCount >= 3) {
      const timeSinceLastAttempt = lastAttemptTime ? now - lastAttemptTime : leadAge;
      
      if (timeSinceLastAttempt > d10) {
        return { 
          status: 'silent', 
          label: 'Silent', 
          icon: '👻', 
          color: 'bg-gray-100 text-gray-600',
          tooltip: `${attemptCount} attempts, no response - ${Math.floor(timeSinceLastAttempt / h24)}d since last attempt`
        };
      }
    }
    
    // Still in attempt loop
    return { 
      status: 'contact_attempted', 
      label: 'Contact Attempted', 
      icon: '📞', 
      color: 'bg-yellow-100 text-yellow-700',
      tooltip: `${attemptCount || 1} attempt(s) - not connected yet`
    };
  }

  // ============================================
  // STEP 7: CONNECTED - Check for HIGH INTENT first
  // 🔥 HEATING UP bypasses the normal flow
  // ============================================
  if (wasConnected || hasAnyContactAttempt) {
    
    // Check for high intent signals → Heating Up (PARALLEL TRACK)
    if (hasHighIntentSignals(lead, sortedInteractions)) {
      return { 
        status: 'heating', 
        label: 'Heating Up', 
        icon: '🔥', 
        color: 'bg-orange-100 text-orange-800',
        tooltip: 'High intent signals - hot lead!'
      };
    }
    
    // Get time since last successful connection
    const lastConnectedTime = (() => {
      // Check lastContactedAt field
      if (lead.lastContactedAt) {
        return new Date(lead.lastContactedAt).getTime();
      }
      
      // Check for connected interaction
      const connectedInteraction = sortedInteractions.find(i => isConnectedOutcome(i.outcome));
      if (connectedInteraction) {
        return new Date(connectedInteraction.date || connectedInteraction.createdAt).getTime();
      }
      
      // Check for any interaction
      if (lastInteraction) {
        return new Date(lastInteraction.date || lastInteraction.createdAt).getTime();
      }
      
      return createdAt;
    })();
    
    const timeSinceConnected = now - lastConnectedTime;

    // ============================================
    // STEP 8: 📞✅ CONTACTED (GREEN) - Fresh connection (< 24h)
    // ============================================
    if (wasConnected && timeSinceConnected < h24) {
      return { 
        status: 'contacted', 
        label: 'Contacted', 
        icon: '📞', 
        color: 'bg-green-100 text-green-700',
        tooltip: 'Successfully spoke to lead - fresh contact'
      };
    }

    // ============================================
    // STEP 9: 📈 ENGAGED (24h - 3d since connection)
    // ============================================
    if (timeSinceConnected >= h24 && timeSinceConnected < d3) {
      return { 
        status: 'engaged', 
        label: 'Engaged', 
        icon: '📈', 
        color: 'bg-green-100 text-green-700',
        tooltip: 'Active working relationship'
      };
    }

    // ============================================
    // STEP 10: 😴 GOING COLD (3-7 days since last activity)
    // ============================================
    if (timeSinceConnected >= d3 && timeSinceConnected < d7) {
      const daysSince = Math.floor(timeSinceConnected / h24);
      return { 
        status: 'cooling', 
        label: 'Going Cold', 
        icon: '😴', 
        color: 'bg-yellow-100 text-yellow-700',
        tooltip: `No activity in ${daysSince}d - follow up!`
      };
    }

    // ============================================
    // STEP 11: ❄️ SLIPPING (7-30 days since last activity)
    // ============================================
    if (timeSinceConnected >= d7 && timeSinceConnected < d30) {
      const daysSince = Math.floor(timeSinceConnected / h24);
      return { 
        status: 'slipping', 
        label: 'Slipping', 
        icon: '❄️', 
        color: 'bg-red-100 text-red-700',
        tooltip: `No activity in ${daysSince}d - at risk!`
      };
    }

    // ============================================
    // STEP 12: 📭 STALE (30+ days since last activity)
    // ============================================
    if (timeSinceConnected >= d30) {
      const daysSince = Math.floor(timeSinceConnected / h24);
      return { 
        status: 'stale', 
        label: 'Stale', 
        icon: '📭', 
        color: 'bg-gray-100 text-gray-600',
        tooltip: `No activity in ${daysSince}d - needs re-engagement`
      };
    }
  }

  // ============================================
  // STEP 13: WHATSAPP-ONLY ENGAGEMENT (no call interactions)
  // ============================================
  
  // 👻 SILENT - Sent messages but zero replies
  if (outboundMsgs.length >= 2 && inboundMsgs.length === 0) {
    return { 
      status: 'silent', 
      label: 'Silent', 
      icon: '👻', 
      color: 'bg-gray-100 text-gray-600',
      tooltip: `${outboundMsgs.length} messages sent, no reply`
    };
  }

  // Has WhatsApp engagement - check recency
  if (lastInbound) {
    const lastReplyTime = new Date(lastInbound.createdAt).getTime();
    const timeSinceReply = now - lastReplyTime;
    
    // 🔥 Replied in last 24h - Heating up!
    if (timeSinceReply < h24) {
      return { 
        status: 'heating', 
        label: 'Heating Up', 
        icon: '🔥', 
        color: 'bg-orange-100 text-orange-800',
        tooltip: 'Replied recently - hot lead!'
      };
    }
    
    // 📈 Replied in last 48h - Engaged
    if (timeSinceReply < h48) {
      return { 
        status: 'engaged', 
        label: 'Engaged', 
        icon: '📈', 
        color: 'bg-green-100 text-green-700',
        tooltip: 'Active conversation in progress'
      };
    }
    
    // 😴 Replied 48-72h ago - Going cold
    if (timeSinceReply < h72) {
      return { 
        status: 'cooling', 
        label: 'Going Cold', 
        icon: '😴', 
        color: 'bg-yellow-100 text-yellow-700',
        tooltip: 'No reply in 48h+ - follow up!'
      };
    }
    
    // ❄️ No reply in 72h+ - Slipping away
    const daysSinceReply = Math.floor(timeSinceReply / h24);
    return { 
      status: 'slipping', 
      label: 'Slipping', 
      icon: '❄️', 
      color: 'bg-red-100 text-red-700',
      tooltip: `No reply in ${daysSinceReply}d - at risk!`
    };
  }

  // Sent WhatsApp but waiting for first reply
  if (lastOutbound) {
    const lastSentTime = new Date(lastOutbound.createdAt).getTime();
    const timeSinceSent = now - lastSentTime;
    
    // ⏳ Waiting (< 24h)
    if (timeSinceSent < h24) {
      return { 
        status: 'waiting', 
        label: 'Waiting', 
        icon: '⏳', 
        color: 'bg-blue-100 text-blue-700',
        tooltip: 'Awaiting first reply'
      };
    }
    
    // 🕐 Pending (24-48h)
    if (timeSinceSent < h48) {
      return { 
        status: 'pending', 
        label: 'Pending', 
        icon: '🕐', 
        color: 'bg-yellow-100 text-yellow-700',
        tooltip: 'No reply yet - 24h+'
      };
    }
    
    // 📭 Stale (48h+)
    return { 
      status: 'stale', 
      label: 'Stale', 
      icon: '📭', 
      color: 'bg-gray-100 text-gray-600',
      tooltip: 'No reply in 48h+'
    };
  }

  // ============================================
  // STEP 14: DEFAULT FALLBACK
  // ============================================
  return { 
    status: 'contacted', 
    label: 'Contacted', 
    icon: '✓', 
    color: 'bg-green-100 text-green-700',
    tooltip: 'Lead has been contacted'
  };
  
}, [messages]);

  const [sheetsStatus, setSheetsStatus] = useState({ isConnected: false });
  const [drips, setDrips] = useState([]);
  const [dripsLoading, setDripsLoading] = useState(false);
  const [showCreateDrip, setShowCreateDrip] = useState(false);
  const [showGoogleSheets, setShowGoogleSheets] = useState(false);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [leadsPerPage, setLeadsPerPage] = useState(25);

// Check authentication on mount
useEffect(() => {
  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (token && userData) {
        // Verify token using existing /me endpoint
        try {
          const response = await fetch(`${config.API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.user) {
              // Update with fresh user data from server
              setUser(data.user);
              localStorage.setItem('user', JSON.stringify(data.user));
              setIsLoggedIn(true);
            } else {
              // Token invalid, clear storage
              localStorage.removeItem('token');
              localStorage.removeItem('user');
            }
          } else {
            // Token invalid, clear storage
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        } catch (error) {
          // If server unavailable, use cached data
          console.log('Server check failed, using cached data');
          setUser(JSON.parse(userData));
          setIsLoggedIn(true);
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
    } finally {
      setAuthLoading(false);
    }
  };

  checkAuth();
}, []);

// Load data when logged in + Auto-polling every 60 seconds
useEffect(() => {
  if (isLoggedIn) {
    // Initial load
    loadWhatsAppStatus();
    loadLeads();
    loadDashboardData(); // ✅ NEW: Load dashboard data
    loadMessages();
    loadSheetsStatus();
    loadDrips();
    loadUsers();
    loadTableViews();
    loadNotifications(); // ✅ NEW: Load notifications on login

    // ✅ NEW: Poll for notification count every 60 seconds
    const notificationInterval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;   // ⏸️ tab hidden: nobody is looking
      loadUnreadCount();
    }, 60000);

    // Cleanup on unmount
    return () => {
      clearInterval(notificationInterval);
    };
  }
}, [isLoggedIn]);

  // Update stats when leads or drips change
  useEffect(() => {
    if (leads.length > 0) {
      const hotCount = leads.filter(l => l.segment === 'hot').length;
      const warmCount = leads.filter(l => l.segment === 'warm').length;
      const coldCount = leads.filter(l => l.segment === 'cold').length;
      setStats(prev => ({
        ...prev,
        totalLeads: leads.length,
        hotLeads: hotCount,
        warmLeads: warmCount,
        coldLeads: coldCount,
        activeDrips: drips.length
      }));
    }
  }, [leads, drips]);

  const loadWhatsAppStatus = async () => {
    try {
      const response = await fetch(`${config.API_URL}/api/whatsapp/status`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setWhatsappStatus(data.data);
    } catch (error) {
      console.error('Error loading WhatsApp status:', error);
    }
  };

  // ✅ SMART POLLER: Silently refreshes leads and messages every 60s
  // but RESPECTS your current page and active filters!
  useEffect(() => {
    if (!isLoggedIn) return;

    // (The "Auto-fetch leads" effect below already loads on every filter/search
    //  change — loading here too sent every request twice.)

    const smartPollInterval = setInterval(async () => {
      if (typeof document !== 'undefined' && document.hidden) return;   // ⏸️ tab hidden: nobody is looking
      try {
        // 1. Fetch leads silently using CURRENT filters
        const leadsRes = await leadsAPI.getAll({
          page: currentPage,
          limit: leadsPerPage,
          ...getCurrentFilterCriteria()
        });
        
        if (leadsRes.data.success) {
          setLeads(leadsRes.data.data);
          setServerPagination(prev => ({
            ...prev,
            totalFiltered: leadsRes.data.totalFiltered || leadsRes.data.data.length,
            totalPages: leadsRes.data.totalPages || 1
          }));
        }

        // 2. Fetch messages silently
        const msgRes = await fetch(`${config.API_URL}/api/messages`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const msgData = await msgRes.json();
        if (msgData.success) setMessages(msgData.data);

      } catch (error) {
        console.error('[Smart Poll] Error:', error);
      }
    }, 180000);   // 3 min. A 1.5s DB query per tab per minute was wasteful.

    return () => clearInterval(smartPollInterval);
  }, [
    isLoggedIn, currentPage, leadsPerPage, leadsSortBy, leadsSortOrder, filterSegment, filterSource, filterAssignedTo,
    filterCallStatus, filterLastOutcome, filterTags, filterDiagnosis, filterTreatmentType, 
    filterPackageName, filterSpenderCategory, filterFollowUpDate, 
    filterDateFrom, filterDateTo, searchQuery, filterHealth, 
    filterLatency, latencyMode, filterAdmissionStage, filterDepartment, filterDealStage, leadsView, filterStatus
  ]);

  // MessagesTab now polls the open thread itself, so no fast poll here.

// ============================================
  // LOAD DASHBOARD DATA - For OwnerDashboard (stats + action center)
  // ============================================
  // Dashboard leads are light copies (17 fields). Always open the full lead, so the window matches
  // the Leads tab — and never open the edit form with a light copy (a save could blank fields).
  const openFullLead = async (lead, open, strict = false) => {
    let full = null;
    try {
      const r = await fetch(`${config.API_URL}/api/leads/${lead._id}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      const d = await r.json();
      if (d && d.success && d.data && d.data._id) full = d.data;
    } catch (e) { /* handled below */ }
    if (!full && strict) { alert('Could not load this lead. Please try again.'); return; }
    setSelectedLead(full || lead);
    open(true);
  };

  const loadDashboardData = async (viewMode = 'all') => {
    setDashboardLoading(true);
    try {
      const response = await leadsAPI.getDashboardData(viewMode);
      if (response.data.success) {
        setDashboardData(response.data.data);
        // keep a copy so the next open is instant
        try { const k = dashCacheKey(); if (k && viewMode === 'all') localStorage.setItem(k, JSON.stringify({ at: Date.now(), data: response.data.data })); } catch (e) { /* storage full: skip */ }
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setDashboardLoading(false);
    }
  };

  // ============================================
  // LOAD LEADS - For Leads Tab (paginated)
  // ============================================
  // counts on the quick-view tabs (All · Mine · New today · Follow-up due · Hot · Unattended)
  const loadViewCounts = async () => {
    try {
      const r = await fetch(`${config.API_URL}/api/leads/view-counts`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }).then(x => x.json());
      if (r.success) setViewCounts(r.data);
    } catch (e) { /* tabs simply show no numbers */ }
  };

  const loadLeads = async (page = currentPage) => {
    const myRequest = ++leadsRequestRef.current;
    setLeadsLoading(true);
    try {
      const response = await leadsAPI.getAll({
        page: page,
        limit: leadsPerPage,
        ...getCurrentFilterCriteria() // 🚨 MAGIC: Sends all filters to backend!
      });
      
      // ✅ A newer search/filter was started while this one ran — ignore this answer
      if (myRequest !== leadsRequestRef.current) return;
      if (response.data.success) {
        setLeads(Array.isArray(response.data.data) ? response.data.data : []);
        setServerPagination({
          page: response.data.page || page,
          totalFiltered: response.data.totalFiltered || 0,
          totalPages: response.data.totalPages || 1
        });
        // a filter that leaves fewer pages: go to the last real page instead of an empty one
        if ((response.data.totalPages || 1) < page) setCurrentPage(Math.max(1, response.data.totalPages || 1));
        loadViewCounts();
      }
    } catch (error) {
      console.error('Error loading leads:', error);
    } finally {
      setLeadsLoading(false);
    }
  };


  // 📋 Role defaults: callers open on "Mine", most urgent first, with the Caller columns
  const isCallerRole = CALLER_ROLES.includes(user?.role);
  useEffect(() => {
    if (!user) return;
    if (leadsView === null) setLeadsView(isCallerRole ? 'mine' : 'all');
    if (isCallerRole && !localStorage.getItem('leadsSortBy_v2')) setLeadsSortBy('urgency');
    let chosen = null; try { chosen = localStorage.getItem('hs_leads_layout'); } catch (e) {}
    if (!chosen && isCallerRole && activeViewId === 'builtin:owner') setActiveViewId('builtin:caller');
  }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // 📞 Phone: after "Call", coming back to Heroshhi asks how the call went
  useEffect(() => {
    const onVis = () => {
      const p = pendingCallRef.current;
      if (document.visibilityState === 'visible' && p && Date.now() - p.at < 3 * 3600 * 1000) {
        pendingCallRef.current = null;
        setLogCallSecs(Math.round((Date.now() - p.at) / 1000));
        setLogCallLead(p.lead);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // ✅ NEW: Auto-fetch leads when filters or pages change!
  useEffect(() => {
    if (isLoggedIn) {
      loadLeads(currentPage);
    }
  }, [
    currentPage, leadsPerPage, leadsSortBy, leadsSortOrder, filterSegment, filterSource, filterAssignedTo,
    filterCallStatus, filterLastOutcome, filterTags, filterDiagnosis, filterTreatmentType, 
    filterPackageName, filterSpenderCategory, filterFollowUpDate, 
    filterDateFrom, filterDateTo, searchQuery, filterHealth, 
    filterLatency, latencyMode, filterAdmissionStage, filterDepartment, filterDealStage, leadsView, filterStatus
  ]);
  
  // silent = refresh in the background without showing the spinner
  const loadMessages = async (silent = false) => {
    if (!silent) setMessagesLoading(true);
    try {
      const response = await fetch(`${config.API_URL}/api/messages`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) { setMessages(Array.isArray(data.data) ? data.data : []); setMessagesMeta(data.inbox || null); }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      if (!silent) setMessagesLoading(false);
    }
  };

  // Show a just-sent message immediately, then confirm from the server.
  const addSentMessage = (payload) => {
    // The send route returns { message, whatsappMessageId }, so unwrap it.
    const msg = payload?.message || payload;

    if (msg && msg._id) {
      setMessages(prev => [msg, ...prev]);
      setTimeout(() => loadMessages(true), 1500);
      return;
    }
    loadMessages(true);
    setTimeout(() => loadMessages(true), 1500);
  };


  // ============================================
  // ✅ LOAD NOTIFICATIONS
  // ============================================
  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await fetch(`${config.API_URL}/api/notifications?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setNotifications(data.data || []);
          setUnreadCount(data.unreadCount || 0);
        }
      }
    } catch (error) {
      console.error('Load notifications error:', error);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await fetch(`${config.API_URL}/api/notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUnreadCount(data.count || 0);
        }
      }
    } catch (error) {
      console.error('Load unread count error:', error);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${config.API_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setNotifications(prev => 
        prev.map(n => n._id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Mark read error:', error);
    }
  };

  const markAllNotificationsAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${config.API_URL}/api/notifications/mark-all-read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Mark all read error:', error);
    }
  };

  const loadSheetsStatus = async () => {
    try {
      const response = await fetch(`${config.API_URL}/api/sheets/status`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setSheetsStatus(data.data);
    } catch (error) {
      console.error('Error loading sheets status:', error);
    }
  };

  const loadDrips = async () => {
    setDripsLoading(true);
    try {
      const response = await fetch(`${config.API_URL}/api/drips`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setDrips(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      console.error('Error loading drips:', error);
    } finally {
      setDripsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await fetch(`${config.API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setAllUsers(Array.isArray(data.data) ? data.data : []);
      } else {
        // Fallback to current user
        const userData = localStorage.getItem('user');
        if (userData) {
          const currentUser = JSON.parse(userData);
          setAllUsers([{
            _id: currentUser._id || currentUser.id,
            name: currentUser.name || currentUser.email,
            email: currentUser.email
          }]);
        }
      }
    } catch (error) {
      console.error('Error loading users:', error);
      // Fallback to current user
      const userData = localStorage.getItem('user');
      if (userData) {
        const currentUser = JSON.parse(userData);
        setAllUsers([{
          _id: currentUser._id || currentUser.id,
          name: currentUser.name || currentUser.email,
          email: currentUser.email
        }]);
      }
    }
  };

  // ============================================
  // TABLE VIEWS API FUNCTIONS
  // ============================================
  
  // Load table views
  const loadTableViews = async () => {
    setViewsLoading(true);
    try {
      const response = await fetch(`${config.API_URL}/api/table-views`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      
      if (data.success) {
        const views = data.data.views || [];
        setTableViews(views);
        // the layout this person last picked; otherwise the built-in one for their role
        let pick = null; try { pick = localStorage.getItem('hs_leads_layout'); } catch (e) {}
        const valid = pick && (pick.startsWith('builtin:') || views.some(v => v._id === pick));
        setActiveViewId(valid ? pick : `builtin:${CALLER_ROLES.includes(user?.role) ? 'caller' : 'owner'}`);
      }
    } catch (error) {
      console.error('Error loading table views:', error);
    } finally {
      setViewsLoading(false);
    }
  };

  // Create new view
  const createTableView = async ({ name, columns, setAsDefault }) => {
    const response = await fetch(`${config.API_URL}/api/table-views`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ name, columns, setAsDefault })
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message);
    await loadTableViews();
  };

  // Update view
  const updateTableView = async (viewId, { name, columns, setAsDefault }) => {
    const response = await fetch(`${config.API_URL}/api/table-views/${viewId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ name, columns, setAsDefault })
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message);
    await loadTableViews();
  };

  // Delete view
  const deleteTableView = async (viewId) => {
    const response = await fetch(`${config.API_URL}/api/table-views/${viewId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message);
    await loadTableViews();
  };

  // Set active view
  const setActiveView = async (viewId) => {
    setActiveViewId(viewId);
    try { localStorage.setItem('hs_leads_layout', viewId); } catch (e) {}
    if (String(viewId).startsWith('builtin:')) return;   // built-in layouts live in the app, not on the server
    try {
      await fetch(`${config.API_URL}/api/table-views/${viewId}/activate`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (error) {
      console.error('Error setting active view:', error);
    }
  };

  // Set default view
  const setDefaultView = async (viewId) => {
    try {
      await fetch(`${config.API_URL}/api/table-views/${viewId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ setAsDefault: true })
      });
      await loadTableViews();
    } catch (error) {
      console.error('Error setting default view:', error);
    }
  };

  // Get visible columns for current view
  const getVisibleColumns = React.useMemo(() => {
    const activeView = [...BUILTIN_LAYOUTS, ...tableViews].find(v => v._id === activeViewId) || tableViews[0];
    if (!activeView?.columns) return DEFAULT_COLUMNS.filter(c => c.visible);
    return activeView.columns.filter(c => c.visible).sort((a, b) => a.order - b.order);
  }, [tableViews, activeViewId]);

  // ✅ SERVER-SIDE CALCULATIONS
  // The backend has already filtered and sliced the array for us!
  const filteredLeads = leads; 
  const paginatedLeads = leads; 
  const totalPages = serverPagination.totalPages;
  const startIndex = (currentPage - 1) * leadsPerPage;
  const endIndex = startIndex + leads.length;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [isLoggedIn, filterSegment, filterSource, filterAssignedTo, filterCallStatus, filterLastOutcome, 
    filterTags, filterDiagnosis, filterTreatmentType, filterPackageName, filterSpenderCategory, filterFollowUpDate, filterDateFrom, filterDateTo, searchQuery,
    filterHealth, filterLatency, latencyMode, filterAdmissionStage, filterDepartment, filterDealStage]);

  // ✅ NEW: Bulk Selection Functions
  const toggleLeadSelection = (leadId) => {
    setSelectedLeadIds(prev => 
       prev.includes(leadId) 
         ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  // Select all on current page only
  const selectAllLeads = () => {
    setSelectedLeadIds(paginatedLeads.map(lead => lead._id));
    setSelectAllMatching(false);
  };

  // ✅ NEW: Select ALL leads matching current filter (across all pages)
  const selectAllMatchingLeads = () => {
    setSelectAllMatching(true);
    setSelectedLeadIds(paginatedLeads.map(lead => lead._id));
  };

  const deselectAllLeads = () => {
    setSelectedLeadIds([]);
    setSelectAllMatching(false);
  };

  const filteredMessages = messages.filter(msg => {
    if (!messageSearch) return true;
    const searchLower = messageSearch.toLowerCase();
    return (
      msg.leadId?.name?.toLowerCase().includes(searchLower) ||
      msg.leadId?.phone?.includes(searchLower) ||
      msg.content?.toLowerCase().includes(searchLower)
    );
  });

const handleLogout = async () => {
    // 📲 stop this phone getting this account's alerts — BEFORE the login is cleared
    await forgetThisDevice();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('originalAdminToken');
    localStorage.removeItem('originalAdminUser');
    setIsLoggedIn(false);
    setUser(null);
    setLeads([]);
    setMessages([]);
    setDrips([]);
  };

  // ============================================
  // ✅ SESSION TIMEOUT - Auto-logout after 30 min inactivity
  // ============================================
  const handleSessionTimeout = useCallback((reason) => {
    console.log('[Security] Session timeout - logging out');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('originalAdminToken');
    localStorage.removeItem('originalAdminUser');
    setIsLoggedIn(false);
    setUser(null);
    setShowTimeoutWarning(false);
    // Optionally redirect to login with message
    alert(reason === 'expired'
      ? 'Your login has expired. Please log in again.'
      : 'Your session has expired due to inactivity. Please log in again.');
  }, []);

  // ✅ Expired login → back to the login page (instead of pages quietly failing)
  // Watches every request to our server; a "401 Unauthorized" on a logged-in
  // request means the login is no longer valid.
  useEffect(() => {
    if (!isLoggedIn) return;
    const originalFetch = window.fetch;
    let handled = false;
    window.fetch = async (...args) => {
      const res = await originalFetch(...args);
      try {
        const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
        const h = args[1]?.headers || {};
        const hasAuth = !!(h.Authorization || h.authorization || (typeof h.get === 'function' && h.get('Authorization')));
        if (res.status === 401 && hasAuth && url.startsWith(config.API_URL) && !handled) {
          handled = true;
          handleSessionTimeout('expired');
        }
      } catch (e) { /* never block the request */ }
      return res;
    };
    return () => { window.fetch = originalFetch; };
  }, [isLoggedIn, handleSessionTimeout]);

  const resetInactivityTimer = useCallback(() => {
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    // Hide warning if it was showing
    setShowTimeoutWarning(false);
    
    // Set new timer (only if logged in)
    if (isLoggedIn) {
      inactivityTimerRef.current = setTimeout(() => {
        // Show warning 2 minutes before logout
        setShowTimeoutWarning(true);
        
        // Auto-logout after 2 more minutes if no activity
        setTimeout(() => {
          if (showTimeoutWarning) {
            handleSessionTimeout();
          }
        }, 2 * 60 * 1000); // 2 minutes grace period
      }, SESSION_TIMEOUT_MS - (2 * 60 * 1000)); // 28 minutes
    }
  }, [isLoggedIn, showTimeoutWarning, handleSessionTimeout, SESSION_TIMEOUT_MS]);

  // Set up activity listeners
  useEffect(() => {
    if (!isLoggedIn) return;
    
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    
    // Throttle to avoid excessive resets
    let lastActivity = Date.now();
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivity > 1000) { // Only reset if >1 second since last activity
        lastActivity = now;
        resetInactivityTimer();
      }
    };
    
    // Add listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity);
    });
    
    // Start initial timer
    resetInactivityTimer();
    
    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [isLoggedIn, resetInactivityTimer]);

  const clearAllFilters = () => {
    setSearchQuery('');
    setSearchInput('');
    setFilterSegment('all');
    setFilterAssignedTo('all');
    setFilterCallStatus('all');
    setFilterLastOutcome('all');
    setFilterAdmissionStage('all');
    setFilterDepartment('all');
    setFilterDealStage('all');
    setFilterTags([]);             // ✅ CHANGED
    setFilterDiagnosis([]);     // ✅ FIXED
    setFilterTreatmentType([]); // ✅ FIXED
    setFilterPackageName([]);   // ✅ FIXED
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterSource('all');        // ✅ Was missing
    setFilterFollowUpDate('all');  // ✅ Was missing
    setFilterHealth('all');        // ✅ NEW
    setFilterLatency('all');
    setFilterSpenderCategory('all');       // ✅ NEW
    setLatencyMode('new');         // ✅ NEW
    setFilterStatus('all');
  };

// ============================================
// ✅ NEW: Active Filter Count & Badges for Collapsible UI
// ============================================
const activeFilterCount = React.useMemo(() => {
  let count = 0;
  if (filterStatus !== 'all') count++;
  if (filterSegment !== 'all') count++;
  if (filterSource !== 'all') count++;
  if (filterFollowUpDate !== 'all') count++;
  if (filterAssignedTo !== 'all') count++;
  if (filterCallStatus !== 'all') count++;
  if (filterLastOutcome !== 'all') count++;
  if (filterTags.length > 0) count++; // ✅ CHANGED
  if (filterDiagnosis.length > 0) count++;      // 👈 FIXED
  if (filterTreatmentType.length > 0) count++;  // 👈 FIXED
  if (filterPackageName.length > 0) count++;    // 👈 FIXED
  if (filterDateFrom) count++;
  if (filterDateTo) count++;
  if (filterAdmissionStage !== 'all') count++;
  if (filterDepartment !== 'all') count++;
  if (filterDealStage !== 'all') count++;
  if (filterHealth !== 'all') count++;
  if (filterLatency !== 'all') count++;
  if (filterSpenderCategory !== 'all') count++; 
  return count;
}, [filterSegment, filterSource, filterFollowUpDate, filterAssignedTo, filterCallStatus, filterLastOutcome, filterTags, filterDiagnosis, filterTreatmentType, filterPackageName, filterDateFrom, filterDateTo, filterAdmissionStage, filterDepartment, filterDealStage, filterHealth, filterLatency, filterSpenderCategory, filterStatus]);

const getActiveFilterBadges = () => {
  const badges = [];
  
  if (filterSegment !== 'all') {
    badges.push({ 
      key: 'segment', 
      label: `Segment: ${filterSegment}`, 
      onRemove: () => setFilterSegment('all') 
    });
  }
  if (filterSource !== 'all') {
    badges.push({ key: 'source', label: `Source: ${filterSource}`, onRemove: () => setFilterSource('all') });
  }
  if (filterFollowUpDate !== 'all') {
    const labels = { overdue: 'Overdue', today: 'Today', upcoming: 'Upcoming', 'no-followup': 'No F/U' };
    badges.push({ key: 'followup', label: labels[filterFollowUpDate] || filterFollowUpDate, onRemove: () => setFilterFollowUpDate('all') });
  }
  if (filterAssignedTo !== 'all') {
    const userName = allUsers.find(u => u._id === filterAssignedTo)?.name || 'User';
    badges.push({ key: 'assigned', label: `Owner: ${userName}`, onRemove: () => setFilterAssignedTo('all') });
  }
  if (filterLastOutcome !== 'all') {
    badges.push({ key: 'lastOutcome', label: `Last outcome: ${LAST_OUTCOME_LABEL[filterLastOutcome] || filterLastOutcome}`, onRemove: () => setFilterLastOutcome('all') });
  }
  if (filterCallStatus !== 'all') {
    badges.push({ key: 'callStatus', label: `Call: ${filterCallStatus === 'no-call-status' ? 'No Status' : filterCallStatus}`, onRemove: () => setFilterCallStatus('all') });
  }
  if (filterTags.length > 0) {
    badges.push({ key: 'tags', label: `${filterTags.length} Tags`, onRemove: () => setFilterTags([]) });
  }
  if (filterDiagnosis.length > 0) {
    badges.push({ key: 'diag', label: `${filterDiagnosis.length} Diagnoses`, onRemove: () => setFilterDiagnosis([]) });
  }
  if (filterTreatmentType.length > 0) {
    badges.push({ key: 'treat', label: `${filterTreatmentType.length} Treatments`, onRemove: () => setFilterTreatmentType([]) });
  }
  if (filterPackageName.length > 0) {
    badges.push({ key: 'pkg', label: `${filterPackageName.length} Packages`, onRemove: () => setFilterPackageName([]) });
  }
  if (filterDateFrom || filterDateTo) {
    const label = filterDateFrom && filterDateTo 
      ? `${filterDateFrom} - ${filterDateTo}`
      : filterDateFrom ? `From ${filterDateFrom}` : `To ${filterDateTo}`;
    badges.push({ key: 'dates', label, onRemove: () => { setFilterDateFrom(''); setFilterDateTo(''); } });
  }
  if (filterHealth !== 'all') {
    const healthLabels = {
      unprocessed: 'Unprocessed', active: 'Active', at_risk: 'At Risk',
      neglected: 'Neglected', archived: 'Archived', heating: 'Heating',
      new: 'New', cooling: 'Cooling', slipping: 'Slipping'
    };
    badges.push({ key: 'health', label: healthLabels[filterHealth] || filterHealth, onRemove: () => setFilterHealth('all') });
  }
  if (filterLatency !== 'all') {
    badges.push({ key: 'latency', label: `Speed: ${filterLatency}`, onRemove: () => setFilterLatency('all') });
  }
  // NEW: Spender Badge 
  if (filterSpenderCategory !== 'all') {
    const labels = { High: 'High Spender', Medium: 'Medium', Low: 'Low', None: 'No Spend' };
    badges.push({ key: 'spender', label: labels[filterSpenderCategory] || filterSpenderCategory, onRemove: () => setFilterSpenderCategory('all') });
  }
  
  return badges.slice(0, 5); // Max 5 badges shown
};

// ============================================
// ✅ GET ALL FILTERS FOR API
// ============================================
const getCurrentFilterCriteria = () => {
  const base = ({
  segment: filterSegment !== 'all' ? filterSegment : undefined,
  source: filterSource !== 'all' ? filterSource : undefined,
  assignedTo: filterAssignedTo !== 'all' ? filterAssignedTo : undefined,
  callStatus: filterCallStatus !== 'all' ? filterCallStatus : undefined,
  lastOutcome: filterLastOutcome !== 'all' ? filterLastOutcome : undefined,
  tags: filterTags.length > 0 ? filterTags.join(',') : undefined,
  diagnosis: filterDiagnosis.length > 0 ? filterDiagnosis.join(',') : undefined, 
  treatmentType: filterTreatmentType.length > 0 ? filterTreatmentType.join(',') : undefined, 
  packageName: filterPackageName.length > 0 ? filterPackageName.join(',') : undefined,
  followUpDate: filterFollowUpDate !== 'all' ? filterFollowUpDate : undefined,
  dateFrom: filterDateFrom || undefined,
  dateTo: filterDateTo || undefined,
  search: searchQuery || undefined,
  spenderCategory: filterSpenderCategory !== 'all' ? filterSpenderCategory : undefined,
  
  // 🚨 FIXED: Added the missing filters so the backend can hear them!
  health: filterHealth !== 'all' ? filterHealth : undefined,
  latency: filterLatency !== 'all' ? filterLatency : undefined,
  latencyMode: latencyMode,
  admissionStage: filterAdmissionStage !== 'all' ? filterAdmissionStage : undefined,
  department: filterDepartment !== 'all' ? filterDepartment : undefined,
  dealStage: filterDealStage !== 'all' ? filterDealStage : undefined,
  sortBy: leadsSortBy,
  sortOrder: leadsSortOrder
});
  // quick view (tabs) and status
  const v = leadsView || (CALLER_ROLES.includes(user?.role) ? 'mine' : 'all');
  const myId = user?._id || user?.id;
  const d = new Date(); const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  if (v === 'mine' && myId) base.assignedTo = myId;
  if (v === 'new_today') { base.dateFrom = today; base.dateTo = today; }
  if (v === 'followup_due') base.followUpDate = 'due';
  if (v === 'hot') base.segment = 'hot';
  if (v === 'unattended') base.unattended = 'true';
  base.stage = filterStatus !== 'all' ? filterStatus : undefined;   // 🧭 the Stage filter (kept the old variable name)
  return base;
};

  // 🛡️ A different filter/view/sort/page size: back to page 1 and clear the selection.
  // (A kept "select all matching" would otherwise apply bulk actions to the NEW filter's leads.)
  const leadsCriteriaKey = JSON.stringify({ ...getCurrentFilterCriteria(), leadsPerPage });
  const lastCriteriaRef = useRef(leadsCriteriaKey);
  useEffect(() => {
    if (lastCriteriaRef.current === leadsCriteriaKey) return;
    lastCriteriaRef.current = leadsCriteriaKey;
    setCurrentPage(1);
    setSelectedLeadIds([]);
    setSelectAllMatching(false);
  }, [leadsCriteriaKey]);


// ============================================
// ✅ NEW: Fetch count of all leads matching current filter
// ============================================
useEffect(() => {
  const fetchMatchingCount = async () => {
    if (!isLoggedIn) return;
    try {
      const response = await fetch(`${config.API_URL}/api/leads/bulk-count`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ filterCriteria: getCurrentFilterCriteria() })
      });
      const data = await response.json();
      if (data.success) setTotalMatchingCount(data.count);
    } catch (err) {
      console.error('Fetch matching count error:', err);
    }
  };
  
  fetchMatchingCount();
}, [isLoggedIn, filterSegment, filterSource, filterAssignedTo, filterCallStatus, filterLastOutcome, 
    filterTags, filterDiagnosis, filterTreatmentType, filterPackageName, filterFollowUpDate, filterDateFrom, filterDateTo, searchQuery, leads.length,
    // ✅ These filters change the matching leads too — the count must refresh
    filterHealth, filterLatency, latencyMode, filterAdmissionStage, filterDepartment, filterDealStage, filterSpenderCategory, leadsView, filterStatus]);

  // ✅ UPDATED: CSV Import Handler
  const handleCSVImport = async (items, importAs = 'leads', leadMode) => {
  console.log(`Starting CSV import with ${items.length} ${importAs}`);
  console.log('API URL:', config.API_URL);
  
  try {
    // ✅ NEW: Handle contacts import separately
    if (importAs === 'contacts') {
      const url = `${config.API_URL}/api/contacts/import`;
      console.log('Importing contacts to:', url);
      
      // Prepare contacts for import
      const contacts = items.map(item => ({
        phone: item.phone,
        name: item.name || '',
        email: item.email || '',
        tags: item.tags || []
      }));
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ contacts })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }
      
      if (data.success) {
        const inserted = data.data?.inserted || 0;
        const updated = data.data?.updated || 0;
        alert(`Contacts import complete!\n• ${inserted} new contacts added\n• ${updated} existing contacts updated`);
        // Note: Contacts tab will refresh on its own
      } else {
        throw new Error(data.message || 'Import failed');
      }
      return;
    }
    
    // Original leads import logic
    const url = `${config.API_URL}/api/leads/import`;
    console.log('Full URL:', url);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      // 📥 'fresh' = new leads to follow up, 'history' = past records
      body: JSON.stringify({ leads: items, mode: leadMode === 'fresh' ? 'fresh' : 'history' })
    });
    
    console.log('Response status:', response.status);
    
    const data = await response.json();
    console.log('Response data:', data);
    
    if (!response.ok) {
      throw new Error(data.message || `HTTP ${response.status}`);
    }
    
    if (data.success) {
      // ✅ Check if this is a background job (>50 leads)
      if (data.data.mode === 'background') {
        console.log('Background import started, job ID:', data.data.jobId);
        
        // Show progress modal instead of alert
        setImportJobId(data.data.jobId);
        setImportTotalLeads(data.data.totalLeads || items.length);
        setShowImportProgress(true);
        setShowCSVImport(false);
        return;
      }
      
      // ✅ Synchronous import - use ?? 0 to handle undefined
      const imported = data.data.imported ?? 0;
      const reactivated = data.data.reactivated ?? 0;
      const skipped = data.data.skipped ?? 0;
      
      alert(`Import complete!\n• ${imported} new leads imported\n• ${reactivated} reactivated\n• ${skipped} skipped`);
      loadLeads();
    } else {
      throw new Error(data.message || 'Import failed');
    }
  } catch (error) {
    console.error('CSV Import error:', error);
    alert('Import failed: ' + error.message);
    throw error;
  }
};

// ✅ Handle import progress modal completion
const handleImportComplete = (results) => {
  console.log('Import completed:', results);
  loadLeads();
  setShowImportProgress(false);
  setImportJobId(null);
  setImportTotalLeads(0);
};

// ============================================
// ✅ NEW: DELETE LEAD FUNCTIONS
// ============================================

// Delete single lead - Admin/Owner only
const handleDeleteLead = async (leadId, leadName) => {
  if (!window.confirm(`Are you sure you want to permanently delete "${leadName}"?\n\nThis action cannot be undone.`)) {
    return;
  }

  try {
    const response = await fetch(`${config.API_URL}/api/leads/${leadId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    const data = await response.json();

    if (data.success) {
      // Remove from local state immediately for better UX
      setLeads(prev => prev.filter(l => l._id !== leadId));
      // Also remove from selection if selected
      setSelectedLeadIds(prev => prev.filter(id => id !== leadId));
    } else {
      alert('Failed to delete lead: ' + (data.message || 'Unknown error'));
    }
  } catch (error) {
    console.error('Delete lead error:', error);
    alert('Error deleting lead: ' + error.message);
  }
};

// Bulk delete selected leads - Admin/Owner only
const handleBulkDelete = async () => {
  // ✅ CHANGED: Use total matching count if selectAllMatching is true
  const deleteCount = selectAllMatching ? totalMatchingCount : selectedLeadIds.length;
  
  if (deleteCount === 0) {
    alert('No leads selected');
    return;
  }

  const confirmMsg = `Are you sure you want to permanently delete ${deleteCount} lead(s)?\n\nThis action cannot be undone.`;
  if (!window.confirm(confirmMsg)) {
    return;
  }

  setDeleting(true);

  try {
    // ✅ CHANGED: Send filter criteria if selectAllMatching, otherwise send IDs
    const requestBody = selectAllMatching 
      ? { selectAllMatching: true, filterCriteria: getCurrentFilterCriteria() }
      : { leadIds: selectedLeadIds };

    const response = await fetch(`${config.API_URL}/api/leads/bulk-delete`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();

    if (data.success) {
      alert(`Successfully deleted ${data.deletedCount} leads`);
      // Remove from local state
      setLeads(prev => prev.filter(l => !selectedLeadIds.includes(l._id)));
      setSelectedLeadIds([]);
    } else {
      alert('Failed to delete leads: ' + (data.message || 'Unknown error'));
    }
  } catch (error) {
    console.error('Bulk delete error:', error);
    alert('Error deleting leads: ' + error.message);
  } finally {
    setDeleting(false);
  }
};

const exportToCSV = async () => {
  if (serverPagination.totalFiltered === 0) {
    alert('No leads to export');
    return;
  }

  alert('Generating full export with all fields... This may take a minute.');

  try {
    // 1. Fetch shallow leads
    const response = await leadsAPI.getAll({
      ...getCurrentFilterCriteria(),
      page: 1,
      limit: 100000 
    });
    
    const shallowLeads = response.data.data || [];
    if (shallowLeads.length === 0) {
      alert('No matching leads found to export');
      return;
    }

    // 2. 🚨 DEEP FETCH: Unearth the hidden historical data arrays!
    const allLeadsToExport = [];
    const batchSize = 20; 
    
    for (let i = 0; i < shallowLeads.length; i += batchSize) {
      const batch = shallowLeads.slice(i, i + batchSize);
      const promises = batch.map(async (lead) => {
        try {
          const res = await fetch(`${config.API_URL}/api/leads/${lead._id}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          const data = await res.json();
          return data.success && data.data ? data.data : lead;
        } catch (err) {
          return lead; 
        }
      });
      
      const fullLeads = await Promise.all(promises);
      allLeadsToExport.push(...fullLeads);
    }

    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const formatDateStr = (dateString) => {
      if (!dateString) return '';
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(String(dateString))) return dateString;
      const d = new Date(dateString);
      if (isNaN(d)) return String(dateString);
      return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
    };

    const fromDate = filterDateFrom ? new Date(filterDateFrom) : null;
    if (fromDate) fromDate.setHours(0, 0, 0, 0);
    const toDate = filterDateTo ? new Date(filterDateTo) : null;
    if (toDate) toDate.setHours(23, 59, 59, 999);

    const businessType = user?.businessType || 'Generic Business';

    // ============================================
    // 3. DYNAMIC FIELD GENERATOR (All your extra fields!)
    // ============================================
    const baseFields = [
      { key: 'name', label: 'Patient/Client Name' },
      { key: 'phone', label: 'Mobile No.' },
      { key: 'email', label: 'Email' },
      { key: 'source', label: 'Source' },
      { key: 'segment', label: 'Segment' },
      { key: 'gender', label: 'Gender' },
      { key: 'city', label: 'City' },
      { key: 'address', label: 'Address' },
      { key: 'callStatus', label: 'Call Status' },
      { key: 'assignedToName', label: 'Assigned To' }
    ];

    let typeFields = [];
    if (businessType === 'Clinic') {
      typeFields = [
        { key: 'dob', label: 'Date of Birth', type: 'date' },
        { key: 'clinic.department', label: 'Department' },
        { key: 'clinic.consultingDoctor', label: 'Consulting DR.' },
        { key: 'clinic.treatmentType', label: 'Treatment' },
        { key: 'clinic.treatmentStatus', label: 'Treatment Status' },
        { key: 'clinic.packageName', label: 'Package' },
        { key: 'clinic.sessionCount', label: 'Session Count' }
      ];
    } else if (businessType === 'School') {
      typeFields = [
        { key: 'school.studentName', label: 'Student Name' },
        { key: 'school.gradeAppliedFor', label: 'Grade Applied For' },
        { key: 'school.academicSession', label: 'Academic Session' },
        { key: 'school.admissionStage', label: 'Admission Stage' }
      ];
    } else {
      typeFields = [
        { key: 'business.companyName', label: 'Company Name' },
        { key: 'business.industry', label: 'Industry' },
        { key: 'business.businessType', label: 'Business Type' },
        { key: 'business.companySize', label: 'Company Size' },
        { key: 'business.dealStage', label: 'Deal Stage' },
        { key: 'business.projectType', label: 'Project Type' }
      ];
    }

    const campaignFields = [
      { key: 'campaignName', label: 'Campaign Name' },
      { key: 'adGroupName', label: 'Ad Group' },
      { key: 'adName', label: 'Ad Name' }
    ];

    const followUpFields = [
      { key: 'nextFollowUpDate', label: 'Next Follow-up Date', type: 'date' },
      { key: 'followUpPriority', label: 'Follow-up Priority' },
      { key: 'createdAt', label: 'Lead Created Date', type: 'date' },
      { key: 'tags', label: 'Tags' }
    ];

    // Combine all Lead Profile fields for the left side of the CSV
    const leadFields = [...baseFields, ...typeFields, ...campaignFields, ...followUpFields];
    
    // Add specific transaction tracking columns for the right side of the CSV
    const transactionHeaders = [
      'Transaction Date', 'Amount', 'Paid Amount', 'Pending Amt', 'Cash', 'Online', 'Transaction Notes'
    ];

    // Final headers row
    const finalHeaders = [...leadFields.map(f => f.label), ...transactionHeaders];

    const userMap = {};
    allUsers.forEach(u => { userMap[u._id] = u.name; userMap[u.id] = u.name; });
    const getNestedValue = (obj, path) => path.split('.').reduce((current, key) => current && current[key] !== undefined ? current[key] : '', obj);

    const rows = [];

    // ============================================
    // 4. MAPPING DATA TO ROWS
    // ============================================
    allLeadsToExport.forEach(lead => {
      // Build the standard lead data (this gets repeated for every transaction row they have)
      const leadRowData = leadFields.map(field => {
        let value = '';
        if (field.key === 'assignedToName') {
          const assignedId = lead.assignedTo?._id || lead.assignedTo;
          value = userMap[assignedId] || lead.assignedTo?.name || '';
        } else if (field.key === 'tags') {
          value = (lead.tags || []).join(' | ');
        } else if (field.key.includes('.')) {
          value = getNestedValue(lead, field.key);
        } else {
          value = lead[field.key];
        }
        return escapeCSV(field.type === 'date' && value ? formatDateStr(value) : value);
      });

      const pendingAmt = lead.financial?.pendingAmount || 0;
      let transactionRows = [];

      // A. PULL REAL FINANCIAL PAYMENTS
      const payments = lead.financial?.payments || [];
      if (payments.length > 0) {
        payments.forEach(p => {
          const pMode = (p.paymentMode || 'Cash').toLowerCase();
          const isCash = pMode.includes('cash');
          const paidAmount = p.amount || 0;

          transactionRows.push({
            date: p.date,
            amount: paidAmount, // Transaction specific amount
            paidAmount: paidAmount,
            pendingAmt: pendingAmt, 
            cash: isCash ? paidAmount : '0',
            online: !isCash && paidAmount > 0 ? `${paidAmount} ${p.paymentMode || 'Online'}` : '0',
            notes: p.notes || ''
          });
        });
      } 

      // B. PULL OLD INTERACTIONS (Fallback for older data structures)
      const interactionsWithAmounts = (lead.interactions || []).filter(i => 
        i.amount !== undefined && i.amount !== null && i.amount !== '' && 
        !payments.some(p => new Date(p.date).getTime() === new Date(i.date || i.createdAt).getTime())
      );
      
      interactionsWithAmounts.forEach(i => {
        const pMode = (i.paymentMode || 'Cash').toLowerCase();
        const isCash = pMode.includes('cash');
        const paidAmount = i.paidAmount !== undefined ? i.paidAmount : (i.amount || 0);

        transactionRows.push({
          date: i.date || i.createdAt,
          amount: i.amount || paidAmount,
          paidAmount: paidAmount,
          pendingAmt: pendingAmt,
          cash: isCash ? paidAmount : '0',
          online: !isCash && paidAmount > 0 ? `${paidAmount} ${i.paymentMode || 'Online'}` : '0',
          notes: i.notes || i.outcome || ''
        });
      });

      // C. FALLBACK: IF NO PAYMENTS EXIST, CREATE 1 MASTER ROW
      if (transactionRows.length === 0) {
        const pMode = (lead.financial?.paymentMode || 'Cash').toLowerCase();
        const isCash = pMode.includes('cash');
        const paidAmount = lead.financial?.paidAmount || 0;
        const totalAmount = lead.financial?.totalAmount || 0;

        transactionRows.push({
          date: lead.createdAt,
          amount: totalAmount || paidAmount,
          paidAmount: paidAmount,
          pendingAmt: pendingAmt,
          cash: isCash ? paidAmount : '0',
          online: !isCash && paidAmount > 0 ? `${paidAmount} ${lead.financial?.paymentMode || 'Online'}` : '0',
          notes: 'Master Profile'
        });
      }

      // Filter by Dashboard Date Range
      if (fromDate || toDate) {
        transactionRows = transactionRows.filter(t => {
          const d = new Date(t.date);
          if (fromDate && d < fromDate) return false;
          if (toDate && d > toDate) return false;
          return true;
        });
      }

      // Combine Lead Data + Transaction Data into CSV
      transactionRows.forEach(t => {
        rows.push([
          ...leadRowData,                            // All Lead Info
          escapeCSV(formatDateStr(t.date)),          // Transaction Date
          escapeCSV(t.amount),                       // Amount
          escapeCSV(t.paidAmount),                   // Paid Amount
          escapeCSV(t.pendingAmt),                   // Pending Amt
          escapeCSV(t.cash),                         // Cash
          escapeCSV(t.online),                       // Online
          escapeCSV(t.notes)                         // Transaction Notes
        ]);
      });
    });

    const csvContent = [finalHeaders.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const dateStr = new Date().toISOString().split('T')[0];
    const typeSlug = (user?.businessType || 'business').toLowerCase().replace(/\s+/g, '_');
    link.download = `transactions_${typeSlug}_${dateStr}.csv`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Export error:', error);
    alert('Failed to export leads: ' + error.message);
  }
};


// ============================================
// EXPORT SELECTED LEADS ONLY
// ============================================

const exportSelectedToCSV = () => {
  if (selectedLeadIds.length === 0) {
    alert('No leads selected for export');
    return;
  }
  
  // Get selected leads
  const selectedLeads = leads.filter(lead => selectedLeadIds.includes(lead._id));
  
  if (selectedLeads.length === 0) {
    alert('No leads selected for export');
    return;
  }

  // Get business type from user
  const businessType = user?.businessType || 'Generic Business';

  // Helper to escape CSV values
  const escapeCSV = (value) => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  // Create user lookup map
  const userMap = {};
  allUsers.forEach(u => {
    userMap[u._id] = u.name;
    userMap[u.id] = u.name;
  });

  // Basic fields for export
  const headers = ['Name', 'Phone', 'Email', 'Source', 'Segment', 'Call Status', 'Assigned To', 'Created Date'];
  
  const rows = selectedLeads.map(lead => {
    const assignedId = lead.assignedTo?._id || lead.assignedTo;
    const assignedName = userMap[assignedId] || lead.assignedTo?.name || '';
    const createdDate = lead.createdAt ? new Date(lead.createdAt).toLocaleDateString('en-CA') : '';
    
    return [
      escapeCSV(lead.name),
      escapeCSV(lead.phone),
      escapeCSV(lead.email),
      escapeCSV(lead.source),
      escapeCSV(lead.segment),
      escapeCSV(lead.callStatus),
      escapeCSV(assignedName),
      escapeCSV(createdDate)
    ];
  });

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  // Create and download file
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  
  const date = new Date().toISOString().split('T')[0];
  link.download = `selected_leads_${date}.csv`;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// ============================================
// BULK EDIT MODAL - For Admin/Owner/Manager
// Supports: Assign To, Source, Segment, Call Status
// ============================================
const BulkEditModal = ({ isOpen, onClose, selectedLeadIds, users, onComplete, selectAllMatching, filterCriteria, totalCount }) => {
  const [field, setField] = useState('');
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Field options
  const fieldOptions = [
    { key: 'assignedTo', label: 'Assign To', icon: '👤' },
    { key: 'source', label: 'Source', icon: '📍' },
    { key: 'segment', label: 'Segment', icon: '🎯' },
    { key: 'callStatus', label: 'Call Status', icon: '📞' },
  ];

  // Value options based on selected field
  const getValueOptions = () => {
    switch (field) {
      case 'assignedTo':
        return users.map(u => ({ value: u._id, label: u.name || u.email }));
      case 'source':
        return [
          { value: 'Manual Entry', label: 'Manual Entry' },
          { value: 'Facebook Lead Ad', label: 'Facebook Lead Ad' },
          { value: 'Instagram', label: 'Instagram' },
          { value: 'Website Form', label: 'Website Form' },
          { value: 'Google Ads', label: 'Google Ads' },
          { value: 'Referral', label: 'Referral' },
          { value: 'WhatsApp Incoming', label: 'WhatsApp Incoming' },
          { value: 'LinkedIn', label: 'LinkedIn' },
          { value: 'Cold Call', label: 'Cold Call' },
          { value: 'Walk-in', label: 'Walk-in' },
        ];
      case 'segment':
        return [
          { value: 'hot', label: 'Hot - High Intent' },
          { value: 'warm', label: 'Warm - Interested' },
          { value: 'cold', label: 'Cold - Low Intent' },
        ];
      case 'callStatus':
        return [
          { value: '', label: 'Clear Status' },
          { value: 'Call Connected – In Discussion', label: 'In Discussion' },
          { value: 'Call Connected – Interested', label: 'Interested' },
          { value: 'Call Connected – Sent Info', label: 'Sent Info' },
          { value: 'Appointment Booked', label: 'Appointment Booked' },
          { value: 'Callback Scheduled', label: 'Callback Scheduled' },
          { value: 'Call Not Connected', label: 'Not Connected' },
          { value: 'Not Interested', label: 'Not Interested' },
          { value: 'Will Visit Later', label: 'Will Visit Later' },
          { value: 'Converted', label: 'Converted' },
          { value: 'Dropped', label: 'Dropped' },
        ];
      default:
        return [];
    }
  };

  const handleSubmit = async () => {
    if (!field) {
      setError('Please select a field to update');
      return;
    }
    
    if (!value && field !== 'callStatus') {
      setError('Please select a value');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const requestBody = selectAllMatching
        ? { selectAllMatching: true, filterCriteria, updates: { [field]: value } }
        : { leadIds: selectedLeadIds, updates: { [field]: value } };

      const response = await fetch(`${config.API_URL}/api/leads/bulk-update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Updated ${data.modifiedCount} leads!`);
        setTimeout(() => {
          onComplete();
          onClose();
        }, 1500);
      } else {
        setError(data.message || 'Failed to update leads');
      }
    } catch (err) {
      setError('Error updating leads: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setField('');
    setValue('');
    setError('');
    setSuccess('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-800">Bulk Edit Leads</h3>
          <button onClick={() => { resetForm(); onClose(); }}>
            <X size={24} className="text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Updating <strong>{totalCount || selectedLeadIds.length}</strong> selected leads
          {selectAllMatching && <span className="text-blue-600 ml-1">(all matching filter)</span>}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            {success}
          </div>
        )}

        {/* Field Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Field to Update</label>
          <div className="grid grid-cols-2 gap-2">
            {fieldOptions.map(option => (
              <button
                key={option.key}
                onClick={() => { setField(option.key); setValue(''); }}
                className={`px-3 py-3 rounded-lg text-sm font-medium border-2 transition-all text-left ${
                  field === option.key 
                    ? 'border-blue-500 bg-blue-50 text-blue-700' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                
                {option.label.replace(option.icon + ' ', '')}
              </button>
            ))}
          </div>
        </div>

        {/* Value Selection */}
        {field && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select New Value
            </label>
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select {fieldOptions.find(f => f.key === field)?.label.replace(/^[^\s]+ /, '')}...</option>
              {getValueOptions().map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Preview */}
        {field && value && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Preview:</strong> Will set <code className="bg-blue-100 px-1 rounded">{field}</code> to{' '}
              <code className="bg-blue-100 px-1 rounded">
                {field === 'assignedTo' 
                  ? users.find(u => u._id === value)?.name || value 
                  : value}
              </code> for {selectedLeadIds.length} leads
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t">
          <button
            onClick={() => { resetForm(); onClose(); }}
            className="hs-btn hs-btn-secondary flex-1"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !field || (!value && field !== 'callStatus')}
            className="hs-btn hs-btn-primary flex-1"
          >
            {loading ? 'Updating...' : `Update ${totalCount || selectedLeadIds.length} Leads`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// BULK TAG UPDATE MODAL
// ============================================
const BulkTagModal = ({ isOpen, onClose, selectedLeadIds, onComplete, selectAllMatching, filterCriteria, totalCount }) => {
  const [action, setAction] = useState('add');
  const [tagInput, setTagInput] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !selectedTags.includes(tag)) {
      setSelectedTags([...selectedTags, tag]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setSelectedTags(selectedTags.filter(t => t !== tagToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const handleSubmit = async () => {
    if (selectedTags.length === 0) {
      setError('Please add at least one tag');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const requestBody = selectAllMatching
        ? { selectAllMatching: true, filterCriteria, action, tags: selectedTags }
        : { leadIds: selectedLeadIds, action, tags: selectedTags };

      const response = await fetch(`${config.API_URL}/api/leads/bulk-tags`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Tags updated for ${data.modifiedCount} leads!`);
        setTimeout(() => {
          onComplete();
        }, 1500);
      } else {
        setError(data.message || 'Failed to update tags');
      }
    } catch (err) {
      setError('Error updating tags: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-800">Update Tags</h3>
          <button onClick={onClose}>
            <X size={24} className="text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Updating tags for <strong>{totalCount || selectedLeadIds.length}</strong> selected leads
          {selectAllMatching && <span className="text-blue-600 ml-1">(all matching filter)</span>}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            {success}
          </div>
        )}

        {/* Action Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setAction('add')}
              className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                action === 'add' 
                  ? 'border-green-500 bg-green-50 text-green-700' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              Add Tags
            </button>
            <button
              onClick={() => setAction('remove')}
              className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                action === 'remove' 
                  ? 'border-red-500 bg-red-50 text-red-700' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              Remove Tags
            </button>
            <button
              onClick={() => setAction('set')}
              className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                action === 'set' 
                  ? 'border-blue-500 bg-blue-50 text-blue-700' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              Replace All
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {action === 'add' && 'Add these tags to existing tags'}
            {action === 'remove' && 'Remove these tags from leads'}
            {action === 'set' && 'Replace all existing tags with these'}
          </p>
        </div>

        {/* Tag Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type tag and press Enter"
              className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
            <button
              onClick={addTag}
              className="hs-btn hs-btn-primary"
            >
              Add
            </button>
          </div>
        </div>

        {/* Selected Tags */}
        {selectedTags.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {selectedTags.map((tag, idx) => (
              <span 
                key={idx}
                className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
              >
                {tag}
                <button onClick={() => removeTag(tag)} className="hover:text-purple-600">
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="hs-btn hs-btn-secondary flex-1"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || selectedTags.length === 0}
            className="hs-btn hs-btn-primary flex-1"
          >
            {loading ? 'Updating...' : `${action === 'add' ? 'Add' : action === 'remove' ? 'Remove' : 'Set'} Tags`}
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================
  // RENDER COLUMN VALUE FOR DYNAMIC TABLE
  // ============================================
  const renderColumnValue = (lead, columnKey) => {
    switch (columnKey) {
      case 'whyNow': {
        const w = whyNow(lead);
        return <span className={`text-sm font-bold whitespace-nowrap ${w.warn ? 'text-orange-800' : 'text-[#10203A]'}`}>{w.text}</span>;
      }
      case 'lastOutcome': {
        const l = lastOutcome(lead);
        return (<div className="min-w-0 max-w-[280px]"><p className="text-sm text-[#10203A] truncate" title={l.text}>{l.text}</p>{l.by && <p className="text-xs text-gray-600 truncate">{l.by}</p>}</div>);
      }
      case 'sourceCampaign': {
        const sc = sourceCampaign(lead);
        return (<div className="min-w-0 max-w-[240px]"><p className="text-sm text-[#10203A] truncate">{sc.source || '—'}</p>{sc.detail && <p className="text-xs text-gray-600 truncate" title={sc.detail}>{sc.detail}</p>}</div>);
      }
      case 'status': {
        // 🧭 the pipeline stage (column key kept as 'status' so saved table layouts still work)
        const st = stageOf(lead);
        return <span className={`text-sm font-semibold ${STAGE_TONE[st.key] || 'text-[#10203A]'}`} title={st.why || undefined}>{st.label}</span>;
      }
      case 'health':
        const health = getLeadHealth(lead);
        return (
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${health.color}`} title={health.tooltip}>
            <span aria-hidden="true" className="inline-block w-2 h-2 rounded-full bg-current opacity-70" /> <span className="hidden sm:inline">{health.label}</span>
          </span>
        );
        
      case 'leadType':
        return (
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            lead.leadType === 'School' ? 'bg-blue-100 text-blue-800' :
            lead.leadType === 'Clinic' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
          }`}>
                        {lead.leadType || 'Generic'}
          </span>
        );
        
      case 'assignedTo':
        return (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-semibold">
                {(lead.assignedTo?.name || 'U').charAt(0).toUpperCase()}
              </span>
            </div>
            <span className="text-sm font-medium text-gray-900 truncate">
              {lead.assignedTo?.name || 'Unassigned'}
            </span>
          </div>
        );
        
      case 'segment':
        return (
          <span className="inline-flex items-center gap-1.5">
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
              lead.segment === 'hot' ? 'bg-red-100 text-red-800' :
              lead.segment === 'warm' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
            }`}>
                            {lead.segment?.toUpperCase()}
            </span>
            {/* 🔄 Stalled lead given another chance by the morning retry job.
                Without this marker they look like random old leads jumping the queue. */}
            {lead.isRetry && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700"
                title={`Retry #${lead.retryCount || 1} — went quiet, given another chance today`}
              >
                Retry
              </span>
            )}
          </span>
        );
        
      case 'callStatus':
        return lead.callStatus ? (
          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
            lead.callStatus === 'Converted' ? 'bg-green-100 text-green-800' :
            lead.callStatus === 'Appointment Booked' ? 'bg-blue-100 text-blue-800' :
            lead.callStatus.includes('Not') ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
          }`}>{lead.callStatus}</span>
        ) : <span className="text-xs text-gray-400">Not set</span>;
        
      case 'stage':
        if (lead.leadType === 'School' && lead.school?.admissionStage) {
          return <span className="text-xs font-medium text-blue-700">{lead.school.admissionStage}</span>;
        }
        if (lead.leadType === 'Clinic' && lead.clinic?.department) {
          return <span className="text-xs font-medium text-green-700">{lead.clinic.department}</span>;
        }
        if (lead.leadType === 'Generic Business' && lead.business?.dealStage) {
          return <span className="text-xs font-medium text-purple-700">{lead.business.dealStage}</span>;
        }
        return <span className="text-xs text-gray-400">-</span>;
        
      case 'createdAt':
        return (
          <div>
            <p className="text-sm text-gray-900">{new Date(lead.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
            <p className="text-xs text-gray-500">{new Date(lead.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
        );
        
      case 'campaignName':
        return lead.campaignName ? <span className="text-sm text-gray-700">{lead.campaignName}</span> : <span className="text-xs text-gray-400">-</span>;
        
      case 'adGroupName':
        return lead.adGroupName ? <span className="text-sm text-gray-700">{lead.adGroupName}</span> : <span className="text-xs text-gray-400">-</span>;
        
      case 'adName':
        return lead.adName ? <span className="text-sm text-gray-700">{lead.adName}</span> : <span className="text-xs text-gray-400">-</span>;
        
      case 'source':
        return lead.source ? <span className="text-sm text-gray-700">{lead.source}</span> : <span className="text-xs text-gray-400">-</span>;
        
      case 'city':
        return lead.city ? <span className="text-sm text-gray-700">{lead.city}</span> : <span className="text-xs text-gray-400">-</span>;
        
      case 'email':
        return lead.email ? <span className="text-sm text-gray-700 truncate max-w-[150px] block">{lead.email}</span> : <span className="text-xs text-gray-400">-</span>;
        
      case 'tags':
        return [].concat(lead.tags || []).length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {[].concat(lead.tags || []).slice(0, 2).map((tag, i) => (
              <span key={i} className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">{tag}</span>
            ))}
            {[].concat(lead.tags || []).length > 2 && <span className="text-xs text-gray-400">+{[].concat(lead.tags || []).length - 2}</span>}
          </div>
        ) : <span className="text-xs text-gray-400">-</span>;
        
      case 'nextFollowUpDate':
        if (!lead.nextFollowUpDate) return <span className="text-xs text-gray-400">Not set</span>;
        const isOverdue = new Date(lead.nextFollowUpDate) < new Date();
        return <span className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-700'}`}>
          {isOverdue && ''}{new Date(lead.nextFollowUpDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
        </span>;
        
      case 'lastContactedAt':
        return lead.lastContactedAt ? 
          <span className="text-sm text-gray-700">{new Date(lead.lastContactedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span> : 
          <span className="text-xs text-gray-400">Never</span>;

      case 'lastInteractionAt': {
        // ✅ the latest of messages, replies, calls and attempts
        const lastActive = [lead.lastInteractionAt, lead.lastContactedAt, lead.lastMessageAt, lead.lastInboundAt, lead.lastAttemptAt]
          .filter(Boolean).map(d => new Date(d)).filter(d => !isNaN(d)).sort((x, y) => y - x)[0];
        if (!lastActive) return <span className="text-xs text-gray-400">No activity</span>;
        const daysDiff = Math.floor((new Date() - new Date(lastActive)) / (1000 * 60 * 60 * 24));
        const color = daysDiff <= 1 ? 'text-green-600' : daysDiff <= 7 ? 'text-yellow-600' : 'text-red-500';
        return (
          <span className={`text-sm font-medium ${color}`}>
            {lastActiveLabel(lastActive)}
          </span>
        );
      }

      // 👇 NEW: Spender Tier Badge 👇
      case 'financial.spenderCategory':
        const cat = lead.financial?.spenderCategory || 'None';
        if (cat === 'None') return <span className="text-xs text-gray-400">-</span>;
        
        return (
          <span className={`inline-flex px-2 py-1 rounded text-xs font-bold ${
            cat === 'High' ? 'bg-green-100 text-green-800 border border-green-200' :
            cat === 'Medium' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
            'bg-gray-100 text-gray-800 border border-gray-200'
          }`}>
            {cat === 'High' ? 'High Spender' : cat === 'Medium' ? 'Medium Spender' : 'Low Spender'}
          </span>
        );
      // --------------------------------
        
      default:
        return <span className="text-xs text-gray-400">-</span>;
    }
  };


  // ============================================
  // MESSAGE INPUT BOX COMPONENT (WAS MISSING)
  // ============================================
  const MessageInputBox = ({ lead, onSent }) => {
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    const handleSend = async (e) => {
      e.preventDefault();
      if (!message.trim() || sending) return;

      setSending(true);
      try {
        const response = await messagesAPI.send({
          leadId: lead._id,
          content: message
        });

        if (response.data.success) {
          setMessage('');
          onSent();
        }
      } catch (err) {
        console.error('Send message error:', err);
        // If template required, show alert
        if (err.response?.data?.message?.includes('template')) {
          alert('First message must use an approved template. Use the "Send Message" button above to select a template.');
        }
      } finally {
        setSending(false);
      }
    };

    return (
      <form onSubmit={handleSend} className="flex gap-2">
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          disabled={sending}
        />
        <button
          type="submit"
          disabled={sending || !message.trim()}
          className="hs-btn hs-btn-primary"
        >
          {sending ? (
            <RefreshCw size={18} className="animate-spin" />
          ) : (
            <Send size={18} />
          )}
        </button>
      </form>
    );
  };

  // ============================================
  // GOOGLE SHEETS MODAL
  // ============================================
  // (GoogleSheetsModal moved out of the main app, above it, so app updates no longer reset it)

  // ============================================
  // CREATE DRIP MODAL
  // ============================================
  // (CreateDripModal moved out of the main app, above it, so app updates no longer reset it)

  // ============================================
  // SEND MESSAGE MODAL
  // ============================================
  // (SendMessageModal moved out of the main app, above it, so app updates no longer reset it)


// ============================================
// RENDER - SHOW LOGIN OR DASHBOARD
// ============================================

// ✅ Show loading spinner while checking auth
if (authLoading) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <img 
          src="/heroshhi-logo.png" 
          alt="Heroshhi" 
          className="h-16 mx-auto mb-4 animate-pulse"
        />
        <div className="animate-spin w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto"></div>
        <p className="text-gray-500 mt-4 text-sm">Loading...</p>
      </div>
    </div>
  );
}

// ⚡ "Connect your AI" consent page (Sign in with Heroshhi) — works logged in or out
if (window.location.pathname === '/connect') return <ConnectAIPage />;

if (!isLoggedIn) {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage onLogin={(userData, token) => {
          localStorage.setItem('user', JSON.stringify(userData));
          setUser(userData);
          setIsLoggedIn(true);
          const back = sessionStorage.getItem('hs_connect_return');   // ⚡ finish connecting an AI app after login
          if (back && back.startsWith('/connect?')) { sessionStorage.removeItem('hs_connect_return'); window.location.replace(back); }
        }} />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

  return (
    <div className="min-h-screen bg-gray-50 flex">


      {/* IMPERSONATION BANNER - Return to Admin */}
      {localStorage.getItem('isImpersonating') === 'true' && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-purple-600 text-white py-2 px-4 flex items-center justify-center gap-4">
          <span className="text-sm font-medium">
            Viewing as: <strong>{user?.businessName || user?.name}</strong>
          </span>
          <button
            onClick={() => {
              const originalToken = localStorage.getItem('originalAdminToken');
              const originalUser = localStorage.getItem('originalAdminUser');
              if (originalToken && originalUser) {
                localStorage.setItem('token', originalToken);
                localStorage.setItem('user', originalUser);
                localStorage.removeItem('originalAdminToken');
                localStorage.removeItem('originalAdminUser');
                localStorage.removeItem('isImpersonating');
                window.location.reload();
              }
            }}
            className="px-3 py-1 bg-white text-purple-600 rounded-lg text-sm font-semibold hover:bg-purple-50 transition-colors"
          >
            ← Return to Admin
          </button>
        </div>
      )}

 {/* ============================================
          MOBILE MENU OVERLAY
          ============================================ */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

            {/* ============================================
          LEFT SIDEBAR - Mobile Responsive
          ============================================ */}
      <aside className={`${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'} w-64 bg-[#F8FAFC] border-r border-gray-200 h-[100dvh] overflow-y-auto flex flex-col transition-all duration-300 fixed left-0 ${localStorage.getItem('isImpersonating') === 'true' ? 'top-10' : 'top-0'} z-50 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {/* Logo Section */}
        <div className={`p-4 border-b border-gray-200 ${sidebarCollapsed ? 'lg:px-2' : ''}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/heroshhi-header-favicon.png" 
                alt="Heroshhi" 
                className={`${sidebarCollapsed ? 'lg:h-10 lg:w-10' : 'h-10'} object-contain`}
              />
              {!sidebarCollapsed && (
                <div>
                  <h1 className="text-lg font-bold text-[#334155]">Heroshhi Pro</h1>
                </div>
              )}
            </div>
            {/* Mobile Close Button */}
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
            >
              <X size={20} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-3">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
              { id: 'leads', label: 'Leads', icon: Users },
              { id: 'messages', label: 'Messages', icon: MessageSquare },
              { id: 'reports', label: 'Reports', icon: TrendingUp },
              { id: 'drips', label: 'Quick Automations', icon: GitBranch },
              { id: 'workflows', label: 'Workflows', icon: Zap },
              { id: 'broadcasts', label: 'Broadcasts', icon: Send },
              { id: 'contacts', label: 'Contacts', icon: Users },
              { id: 'appointments', label: 'Appointments', icon: Calendar },
              ...(storeConnected ? [{ id: 'orders', label: 'Orders', icon: Layers }] : []),
              ...(webChatOn ? [{ id: 'webchats', label: 'Website chats', icon: MessageCircle }] : []),
              ...(user?.businessType === 'School' ? [{ id: 'fees', label: 'Fees', icon: DollarSign }] : []),
              // WhatsApp Config - Admin/Owner only
              ...((user?.email?.toLowerCase() === 'dducme@gmail.com' || 
                  user?.role === 'admin' || 
                  user?.role === 'owner') ? [
                { id: 'whatsapp-config', label: 'WhatsApp Config', icon: MessageCircle }
              ] : []),
              // Support tab - ONLY for Super Admin
              ...(user?.email?.toLowerCase() === 'dducme@gmail.com' ? [
                { id: 'support', label: 'Support', icon: MessageCircle }
              ] : []),
              // Platform tab - ONLY for Super Admin
              ...(user?.email?.toLowerCase() === 'dducme@gmail.com' ? [
                { id: 'platform', label: 'Platform', icon: Shield }
              ] : []),
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map(item => (
              <li key={item.id}>
                <button
                  onClick={() => { setActiveTab(item.id); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all min-h-[44px] ${
                    activeTab === item.id
                      ? 'bg-[#10203A] text-white shadow-sm'
                      : 'text-[#334155] hover:bg-gray-100 hover:text-[#10203A]'
                  } ${sidebarCollapsed ? 'justify-center' : ''}`}
                  title={sidebarCollapsed ? item.label : ''}
                >
                  {BRAND_ICON_NAMES.includes(item.id)
                    ? <BrandIcon name={item.id} size={20} active={activeTab === item.id} className="flex-none" />
                    : <item.icon size={20} />}
                  {!sidebarCollapsed && <span className="font-medium">{item.label}</span>}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Profile & Collapse Button */}
        <div className="border-t border-gray-200 p-4">
          {/* User Info */}
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3 mb-4 px-2">
              <div className="w-10 h-10 rounded-full bg-[#F59E0B] flex items-center justify-center text-white font-bold">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#334155] truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.role || 'User'}</p>
              </div>
            </div>
          )}

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[#334155] hover:bg-red-50 hover:text-red-600 transition-colors ${sidebarCollapsed ? 'justify-center' : ''}`}
            title={sidebarCollapsed ? 'Logout' : ''}
          >
            <LogOut size={18} />
            {!sidebarCollapsed && <span className="text-sm">Logout</span>}
          </button>

          {/* Collapse Toggle */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full mt-3 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-[#334155] transition-colors"
          >
            {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            {!sidebarCollapsed && <span className="text-sm">Collapse</span>}
          </button>
        </div>
      </aside>

      {/* ============================================
          MAIN CONTENT AREA
          ============================================ */}
     <div className={`flex-1 min-w-0 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'} ml-0 ${localStorage.getItem('isImpersonating') === 'true' ? 'pt-10' : ''} transition-all duration-300`}>
        {/* Top Header Bar */}
        <header className="bg-white shadow-sm border-b sticky top-0 z-30">
          <div className="px-4 lg:px-6 py-3 lg:py-4 flex items-center justify-between">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 -ml-2 mr-2 hover:bg-gray-100 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <Menu size={24} className="text-gray-600" />
            </button>
            
            <div className="flex-1 min-w-0">
              <h2 className="text-lg lg:text-xl font-bold text-gray-800 truncate">
                {activeTab === 'dashboard' && 'Dashboard'}
                {activeTab === 'leads' && 'Leads'}
                {activeTab === 'messages' && 'Messages'}
                {activeTab === 'reports' && 'Reports'}
                {activeTab === 'drips' && 'Quick Automations'}
                {activeTab === 'workflows' && 'Workflows'}
                {activeTab === 'broadcasts' && 'Broadcasts'}
                {activeTab === 'contacts' && 'Contacts'}
                {activeTab === 'appointments' && 'Appointments'}
                {activeTab === 'orders' && 'Orders'}
                {activeTab === 'webchats' && 'Website chats'}
                {activeTab === 'fees' && 'Fees'}
                {activeTab === 'whatsapp-config' && 'WhatsApp Configuration'}
                {activeTab === 'settings' && 'Settings'}
                {activeTab === 'support' && 'Support Admin'}
                {activeTab === 'platform' && 'Platform Admin'}
              </h2>
              <p className="text-sm text-gray-500">{user?.businessName || 'Lead Management Platform'}</p>
            </div>
            <div className="flex items-center gap-4">
              {/* Live Indicator */}
              <div className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-green-700">Live</span>
              </div>

              {/* Notifications Bell + Dropdown */}
              <div className="relative">
                <button 
                  onClick={() => {
                    setShowNotificationPanel(!showNotificationPanel);
                    if (!showNotificationPanel) loadNotifications();
                  }}
                  className="relative p-2 hover:bg-gray-100 rounded-lg"
                >
                  <Bell size={20} className="text-gray-600" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown Panel */}
                {showNotificationPanel && (
                  <>
                    {/* Backdrop */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowNotificationPanel(false)}
                    />
                    
                    {/* Panel */}
                    <div className="absolute right-0 top-12 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
                      {/* Header */}
                      <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                        <h3 className="font-semibold text-gray-800">Notifications</h3>
                        <div className="flex items-center gap-2">
                          {unreadCount > 0 && (
                            <button
                              onClick={markAllNotificationsAsRead}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Mark all as read
                            </button>
                          )}
                          <button 
                            onClick={() => setShowNotificationPanel(false)}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <X size={16} className="text-gray-500" />
                          </button>
                        </div>
                      </div>

                      {/* Notifications List */}
                      <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="py-12 text-center">
                            <Bell size={40} className="mx-auto text-gray-300 mb-3" />
                            <p className="text-gray-500">No notifications yet</p>
                          </div>
                        ) : (
                          notifications.map((notification) => (
                            <div
                              key={notification._id}
                              onClick={() => {
                                if (!notification.isRead) {
                                  markNotificationAsRead(notification._id);
                                }
                                if (notification.actionUrl) {
                                  // actionUrl looks like "/leads/<id>" or "/messages/<id>".
                                  // Pull out the tab and, if there's a lead id, open that lead.
                                  const parts = notification.actionUrl.split('/').filter(Boolean);
                                  const tab = parts[0] || 'dashboard';
                                  const leadId = parts[1];
                                  const known = ['dashboard', 'leads', 'messages', 'contacts', 'broadcasts', 'drips', 'reports', 'settings', 'whatsapp', 'workflows', 'appointments'];
                                  setActiveTab(known.includes(tab) ? tab : 'dashboard');
                                  if (leadId) {
                                    // Not always on the current page — fetch it if needed
                                    const openLead = (lead) => {
                                      setSelectedLead(lead);
                                      if (tab === 'leads') setShowViewLead(true);
                                    };
                                    const local = leads.find(l => l._id === leadId);
                                    if (local) openLead(local);
                                    else {
                                      fetch(`${config.API_URL}/api/leads/${leadId}`, {
                                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                      }).then(r => r.json()).then(d => {
                                        const lead = d.data?.lead || d.data || d.lead;
                                        if (lead && lead._id) openLead(lead);
                                      }).catch(() => {});
                                    }
                                  }
                                  setShowNotificationPanel(false);
                                }
                              }}
                              className={`px-4 py-3 border-b hover:bg-gray-50 cursor-pointer transition-colors ${
                                !notification.isRead ? 'bg-blue-50' : ''
                              }`}
                            >
                              <div className="flex gap-3">
                                <span className="text-xl flex-shrink-0">{notification.icon || '🔔'}</span>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm ${!notification.isRead ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                    {notification.title}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {new Date(notification.createdAt).toLocaleDateString('en-IN', {
                                      day: 'numeric',
                                      month: 'short',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                                {!notification.isRead && (
                                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2"></div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Footer */}
                      {notifications.length > 0 && (
                        <div className="px-4 py-2 bg-gray-50 border-t text-center">
                          <button 
                            onClick={() => {
                              setShowNotificationPanel(false);
                            }}
                            className="text-sm text-gray-500 hover:text-gray-700"
                          >
                            Close
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* User Info */}
              <div className="hidden md:block text-right">
                <p className="text-sm font-medium text-gray-800">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
          </div>
        </header>

      {/* Main Content */}
      <main className="p-3 sm:p-6 pb-24 sm:pb-6 min-w-0" style={{ overflowX: 'clip' }}>
        <ErrorBoundary resetKey={activeTab}>
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && isPhone && ['sales', 'salesperson', 'staff', 'user'].includes(user?.role) && (
          <Suspense fallback={null}>
            <CallQueue />
          </Suspense>
        )}

        {activeTab === 'dashboard' && (
          <Suspense fallback={<LazyLoadFallback type="tab" />}>
          <OwnerDashboard
            dashboardData={dashboardData}        // ✅ NEW: Server-provided data
            dashboardLoading={dashboardLoading}  // ✅ NEW: Dashboard loading state
            leads={leads}                        // ✅ ADD: For funnel metrics
            leadsLoading={leadsLoading}          // ✅ ADD: For loading state
            messages={messages}
            users={allUsers}
            user={user}
            drips={drips}
            whatsappStatus={whatsappStatus}
            onNavigateToLeads={() => setActiveTab('leads')}
            onOpenLead={(lead) => openFullLead(lead, setShowViewLead)}
            onSendMessage={(lead) => openFullLead(lead, setShowSendMessage)}
            onAssignLead={(lead) => openFullLead(lead, setShowEditLead, true)}
            onCreateDrip={() => setActiveTab('drips')}
            onAddLead={() => setShowAddLead(true)}
            onImportCSV={() => setShowCSVImport(true)}
            onGoToSettings={() => setActiveTab('settings')}
            onGoToWhatsAppConfig={() => setActiveTab('whatsapp-config')}
            loadDashboardData={loadDashboardData}
            loadLeads={loadLeads}
            loadMessages={loadMessages}
          />
          </Suspense> 
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div className="space-y-4">
            {/* ============ LEADS: header, quick views, toolbar ============ */}
            {(() => {
              const selCount = selectAllMatching ? totalMatchingCount : selectedLeadIds.length;
              const isOwnerish = ['admin', 'owner', 'manager'].includes(user?.role) || !user?.role;
              const views = [
                ...(isCallerRole ? [['mine', 'Mine', viewCounts?.mine]] : [['all', 'All', viewCounts?.all], ['mine', 'Mine', viewCounts?.mine]]),
                ['new_today', 'New today', viewCounts?.newToday],
                ['followup_due', 'Follow-up due', viewCounts?.followUpDue],
                ['hot', 'Hot', viewCounts?.hot],
                ['unattended', 'Past deadline', viewCounts?.unattended, true],
                ...(isCallerRole ? [['all', 'All', viewCounts?.all]] : [])
              ];
              const activeView = leadsView || (isCallerRole ? 'mine' : 'all');
              const pickView = (k) => { setLeadsView(k); try { localStorage.setItem('hs_leads_view', k); } catch (e) {} };
              const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-IN') : '');
              const total = serverPagination.totalFiltered || 0;
              const anyFilter = activeFilterCount > 0 || !!searchQuery || activeView !== 'all';
              return (<>
                {/* header */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h1 className="text-2xl font-extrabold text-[#10203A]">{isCallerRole ? 'My leads' : 'Leads'}</h1>
                    <p className="text-sm text-gray-600">{fmt(total)} {anyFilter ? `match${total === 1 ? 'es' : ''} this view` : (total === 1 ? 'lead' : 'leads')}</p>
                  </div>
                  <div className="flex items-center gap-2 relative">
                    <button type="button" onClick={() => setLeadsMoreOpen(o => !o)} aria-label="More: import, export, WhatsApp a group" aria-expanded={leadsMoreOpen}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl border border-gray-300 bg-white text-[#10203A] hover:bg-gray-50">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>
                    </button>
                    {leadsMoreOpen && (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setLeadsMoreOpen(false)} />
                        <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-xl z-40 py-1">
                          <button type="button" onClick={() => { setLeadsMoreOpen(false); setShowCSVImport(true); }} className="w-full min-h-[44px] px-4 text-left text-sm font-semibold text-[#10203A] hover:bg-gray-50 flex items-center gap-2"><Upload size={16} /> Import CSV</button>
                          <button type="button" onClick={() => { setLeadsMoreOpen(false); exportToCSV(); }} className="w-full min-h-[44px] px-4 text-left text-sm font-semibold text-[#10203A] hover:bg-gray-50 flex items-center gap-2"><Download size={16} /> Export</button>
                          <button type="button" onClick={() => { setLeadsMoreOpen(false); setShowBulkMessage(true); }} className="w-full min-h-[44px] px-4 text-left text-sm font-semibold text-[#10203A] hover:bg-gray-50 flex items-center gap-2"><MessageSquare size={16} /> WhatsApp a group</button>
                        </div>
                      </>
                    )}
                    <button type="button" onClick={() => setShowAddLead(true)} className="min-h-[44px] px-4 rounded-xl bg-[#10203A] text-white text-sm font-bold hover:bg-[#0b1729] flex items-center gap-1.5"><Plus size={16} /> Add lead</button>
                  </div>
                </div>

                {/* quick views: tabs on computers, wrapping boxes on phones */}
                <nav className="hidden md:flex flex-wrap gap-1 border-b border-gray-200">
                  {views.map(([k, label, n, warn]) => (
                    <button key={k} type="button" onClick={() => pickView(k)}
                      className={`min-h-[44px] px-3 border-b-[3px] text-[15px] whitespace-nowrap ${activeView === k ? 'border-[#10203A] font-extrabold text-[#10203A]' : 'border-transparent font-semibold text-gray-600 hover:text-[#10203A]'}`}>
                      {label}{typeof n === 'number' && <span className={`ml-1.5 font-bold ${warn && n > 0 ? 'text-orange-700' : 'text-gray-500'}`}>{fmt(n)}</span>}
                    </button>
                  ))}
                </nav>
                <nav className="md:hidden flex flex-wrap gap-2">
                  {views.map(([k, label, n, warn]) => (
                    <button key={k} type="button" onClick={() => pickView(k)}
                      className={`flex-auto min-h-[42px] px-3 rounded-xl border text-sm font-bold whitespace-nowrap ${activeView === k ? 'bg-[#10203A] border-[#10203A] text-white' : 'bg-white border-gray-300 text-[#10203A]'}`}>
                      {label}{typeof n === 'number' && <span className={`ml-1 font-extrabold ${warn && n > 0 ? (activeView === k ? 'text-[#F7C860]' : 'text-orange-700') : (activeView === k ? 'text-slate-300' : 'text-gray-500')}`}>{fmt(n)}</span>}
                    </button>
                  ))}
                </nav>

                {/* toolbar, or the selection bar when leads are ticked */}
                {selCount > 0 ? (
                  <div className="flex flex-wrap items-center gap-2 bg-[#10203A] text-white rounded-xl px-3 py-2">
                    <span className="text-sm font-extrabold mr-1">{fmt(selCount)} selected{selectAllMatching ? ' (all matching)' : ''}</span>
                    <button type="button" onClick={() => setShowBulkMessage(true)} className="min-h-[38px] px-3 rounded-lg bg-[#F2B12A] text-[#10203A] text-sm font-bold flex items-center gap-1.5"><MessageSquare size={15} /> WhatsApp</button>
                    <button type="button" onClick={() => setShowBulkTagModal(true)} className="min-h-[38px] px-3 rounded-lg bg-white text-[#10203A] text-sm font-bold flex items-center gap-1.5"><Hash size={15} /> Tags</button>
                    {isOwnerish && <button type="button" onClick={() => setShowBulkEditModal(true)} className="min-h-[38px] px-3 rounded-lg bg-white text-[#10203A] text-sm font-bold flex items-center gap-1.5"><Edit2 size={15} /> Edit</button>}
                    {(user?.role === 'admin' || user?.role === 'owner') && <button type="button" onClick={handleBulkDelete} disabled={deleting} className="min-h-[38px] px-3 rounded-lg bg-white text-red-700 text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"><Trash2 size={15} /> {deleting ? 'Deleting…' : 'Delete'}</button>}
                    <button type="button" onClick={deselectAllLeads} className="ml-auto min-h-[38px] px-2 text-sm font-bold text-slate-300 hover:text-white">Clear</button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="flex-1 basis-full sm:basis-72 flex items-center gap-2 min-h-[44px] px-3 bg-white border border-gray-300 rounded-xl text-gray-500 focus-within:border-[#10203A]">
                      <Search size={18} />
                      <input type="search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search name, phone or email" aria-label="Search leads"
                        className="flex-1 min-w-0 border-0 outline-none bg-transparent text-[15px] text-[#10203A]" />
                    </label>
                    <button type="button" onClick={() => setFiltersExpanded(true)}
                      className={`flex-1 sm:flex-none min-h-[44px] px-3 rounded-xl border text-sm font-bold flex items-center justify-center gap-1.5 ${activeFilterCount > 0 ? 'border-[#10203A] bg-slate-50 text-[#10203A]' : 'border-gray-300 bg-white text-[#10203A]'}`}>
                      <Filter size={16} /> Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
                    </button>
                    <label className="flex-1 sm:flex-none flex items-center min-h-[44px] px-2 rounded-xl border border-gray-300 bg-white">
                      <span className="sr-only">Sort</span>
                      <select value={leadsSortBy} onChange={(e) => { setLeadsSortBy(e.target.value); localStorage.setItem('leadsSortBy_v2', e.target.value); setCurrentPage(1); }}
                        className="w-full bg-transparent border-0 text-sm font-bold text-[#10203A] focus:ring-0">
                        <option value="urgency">Sort: Most urgent first</option>
                        <option value="createdAt">Sort: Newest</option>
                        <option value="updatedAt">Sort: Recently updated</option>
                        <option value="lastInteractionAt">Sort: Last active</option>
                        <option value="name">Sort: Name (A–Z)</option>
                      </select>
                    </label>
                    {leadsSortBy !== 'urgency' && (
                      <button type="button" onClick={() => setLeadsSortOrder(leadsSortOrder === 'desc' ? 'asc' : 'desc')}
                        aria-label={leadsSortOrder === 'desc' ? 'Showing newest first; switch to oldest first' : 'Showing oldest first; switch to newest first'}
                        className="min-h-[44px] min-w-[44px] rounded-xl border border-gray-300 bg-white text-[#10203A] font-bold">{leadsSortOrder === 'desc' ? '↓' : '↑'}</button>
                    )}
                    <div className="hidden md:block">
                      <ViewSelector
                        views={[...BUILTIN_LAYOUTS, ...tableViews]}
                        activeViewId={activeViewId}
                        onSelectView={setActiveView}
                        onCreateNew={() => { setEditingView(null); setIsCreatingNewView(true); setShowColumnCustomizer(true); }}
                        onEditView={(view) => { setEditingView(view); setIsCreatingNewView(false); setShowColumnCustomizer(true); }}
                        onDeleteView={deleteTableView}
                        onSetDefault={setDefaultView}
                        loading={viewsLoading}
                      />
                    </div>
                  </div>
                )}

                {/* active filters as removable labels */}
                {(activeFilterCount > 0) && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {filterStatus !== 'all' && (
                      <span className="inline-flex items-center gap-1 min-h-[32px] pl-2.5 pr-1 rounded-lg bg-slate-100 text-sm font-semibold text-[#10203A]">
                        Stage: {STAGE_LABELS[filterStatus] || filterStatus}
                        <button type="button" aria-label="Remove status filter" onClick={() => setFilterStatus('all')} className="min-h-[28px] min-w-[28px] flex items-center justify-center text-gray-600 hover:text-red-700"><X size={13} /></button>
                      </span>
                    )}
                    {getActiveFilterBadges().map(badge => (
                      <span key={badge.key} className="inline-flex items-center gap-1 min-h-[32px] pl-2.5 pr-1 rounded-lg bg-slate-100 text-sm font-semibold text-[#10203A]">
                        {String(badge.label).replace(/^[^\p{L}\p{N}]+\s*/u, '')}
                        <button type="button" aria-label={`Remove ${badge.key} filter`} onClick={badge.onRemove} className="min-h-[28px] min-w-[28px] flex items-center justify-center text-gray-600 hover:text-red-700"><X size={13} /></button>
                      </span>
                    ))}
                    <button type="button" onClick={clearAllFilters} className="min-h-[32px] px-1 text-sm font-bold text-gray-600 underline underline-offset-4 decoration-gray-300">Clear all</button>
                  </div>
                )}

                {/* filters panel: from the right on computers, full screen on phones */}
                {filtersExpanded && (
                  <div className="fixed inset-0 z-[55] flex justify-end">
                    <div className="absolute inset-0 bg-[#10203A]/40" onClick={() => setFiltersExpanded(false)} />
                    <aside aria-label="Filters" className="relative bg-white w-full sm:w-[460px] h-[100dvh] flex flex-col shadow-2xl">
                      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-200" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
                        <h2 className="text-xl font-extrabold text-[#10203A]">Filters</h2>
                        <button type="button" onClick={() => setFiltersExpanded(false)} aria-label="Close filters" className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl border border-gray-300 text-[#10203A]"><X size={20} /></button>
                      </div>
                      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                        <div>
                          <label htmlFor="flt-status" className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Stage</label>
                          <select id="flt-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="w-full min-h-[42px] px-3 border border-gray-300 rounded-lg bg-white text-sm">
                            <option value="all">All stages</option>
                            {STAGES.map(s => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                          </select>
                        </div>
      {/* Row 1: Date Range */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">From Date</label>
          <input
            type="date"
            value={filterDateFrom}
            onChange={(e) => {
              const newFrom = e.target.value;
              if (filterDateTo && newFrom > filterDateTo) {
                setFilterDateFrom(filterDateTo);
                setFilterDateTo(newFrom);
              } else {
                setFilterDateFrom(newFrom);
              }
            }}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">To Date</label>
          <input
            type="date"
            value={filterDateTo}
            onChange={(e) => {
              const newTo = e.target.value;
              if (filterDateFrom && newTo < filterDateFrom) {
                setFilterDateTo(filterDateFrom);
                setFilterDateFrom(newTo);
              } else {
                setFilterDateTo(newTo);
              }
            }}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all"
          />
        </div>
      </div>

      {/* Row 2: Primary Filters */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Segment</label>
          <select
            value={filterSegment}
            onChange={(e) => setFilterSegment(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-all"
          >
            <option value="all">All Segments</option>
            <option value="hot">Hot</option>
            <option value="warm">Warm</option>
            <option value="cold">Cold</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Source</label>
          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-all"
          >
            <option value="all">All Sources</option>
            {uniqueSources.map(source => (
              <option key={source} value={source}>{source}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Follow-up</label>
          <select
            value={filterFollowUpDate}
            onChange={(e) => setFilterFollowUpDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-all"
          >
            <option value="all">All Follow-ups</option>
            <option value="overdue">Overdue</option>
            <option value="today">Today</option>
            <option value="upcoming">Next 7 Days</option>
            <option value="no-followup">No Follow-up</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Assigned To</label>
          <select
            value={filterAssignedTo}
            onChange={(e) => setFilterAssignedTo(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-all"
          >
            <option value="all">All Sales People</option>
            {allUsers.map(u => (
              <option key={u._id} value={u._id}>{u.name || u.email}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Row 3: Secondary Filters */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="filter-last-outcome" className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Last outcome</label>
          <select id="filter-last-outcome" value={filterLastOutcome} onChange={(e) => setFilterLastOutcome(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-all">
            <option value="all">Any outcome</option>
            {Object.entries(LAST_OUTCOME_LABEL).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Call Status</label>
          <select
            value={filterCallStatus}
            onChange={(e) => setFilterCallStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-all"
          >
            <option value="all">All Status</option>
            <option value="no-call-status">No Call Status</option>
            <option value="Call Connected – In Discussion">In Discussion</option>
            <option value="Call Connected – Interested">Connected - Interested</option>
            <option value="Call Connected – Sent Info">Sent Info</option>
            <option value="Appointment Booked">Appointment Booked</option>
            <option value="Callback Scheduled">Callback Scheduled</option>
            <option value="Call Not Connected">Not Connected</option>
            <option value="Not Interested">Not Interested</option>
            <option value="Will Visit Later">Will Visit Later</option>
            <option value="Converted">Converted</option>
            <option value="Dropped">Dropped</option>
          </select>
        </div>

        {/* Beautiful Excel Tag Filter */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Tags</label>
          <ExcelFilterDropdown inline 
            options={uniqueTags}
            selectedValues={filterTags}
            onChange={setFilterTags}
            placeholder="Select Tags..."
            label="tags"
          />
        </div>

        {/* Type-Specific Filter */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
            {user?.businessType === 'School' ? 'Admission Stage' : 
            user?.businessType === 'Clinic' ? 'Department' : 'Deal Stage'}
          </label>
          
          {user?.businessType === 'School' && (
            <select
              value={filterAdmissionStage}
              onChange={(e) => setFilterAdmissionStage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-all"
            >
              <option value="all">All Stages</option>
              <option value="Inquiry">Inquiry</option>
              <option value="Counseling Done">Counseling Done</option>
              <option value="Campus Visit">Campus Visit</option>
              <option value="Form Submitted">Form Submitted</option>
              <option value="Fees Discussion">Fees Discussion</option>
              <option value="Admission Confirmed">Confirmed</option>
              <option value="Dropped">Dropped</option>
            </select>
          )}
          
          {user?.businessType === 'Clinic' && (
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-all"
            >
              <option value="all">All Departments</option>
              <option value="Dermatology">Dermatology</option>
              <option value="Hair Transplant">Hair Transplant</option>
              <option value="Cosmetic Surgery">Cosmetic Surgery</option>
              <option value="Dental">Dental</option>
              <option value="Physiotherapy">Physiotherapy</option>
              <option value="General Medicine">General Medicine</option>
              <option value="Cardiology">Cardiology</option>
              <option value="Orthopedics">Orthopedics</option>
              <option value="Neurology">Neurology</option>
              <option value="Pediatrics">Pediatrics</option>
              <option value="Other">Other</option>
            </select>
          )}
          
          {user?.businessType !== 'School' && user?.businessType !== 'Clinic' && (
            <select
              value={filterDealStage}
              onChange={(e) => setFilterDealStage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-all"
            >
              <option value="all">All Stages</option>
              <option value="New Lead">New Lead</option>
              <option value="Discovery Call">Discovery Call</option>
              <option value="Proposal Sent">Proposal Sent</option>
              <option value="Negotiation">Negotiation</option>
              <option value="Closed Won">Closed Won</option>
              <option value="Closed Lost">Closed Lost</option>
            </select>
          )}
        </div>

        {/* Spender Tier Dropdown */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Spender Tier</label>
          <select
            value={filterSpenderCategory}
            onChange={(e) => setFilterSpenderCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-all"
          >
            <option value="all">All Tiers</option>
            <option value="High">High Spender</option>
            <option value="Medium">Medium Spender</option>
            <option value="Low">Low Spender</option>
            <option value="None">No Spend History</option>
          </select>
        </div>
      </div>

      {/* Row 4: Health & Latency Filters */}
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Activity</label>
          <select
            value={filterHealth}
            onChange={(e) => setFilterHealth(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-all"
          >
            <option value="all">All Health</option>
            <optgroup label="Groups">
              <option value="unprocessed">Unprocessed (New, Waiting)</option>
              <option value="active">Active (Contacted, Engaged)</option>
              <option value="at_risk">At Risk (Attempted, Cooling)</option>
              <option value="neglected">Neglected (Stale, Silent)</option>
              <option value="archived">Archived (Converted, Dropped)</option>
            </optgroup>
            <optgroup label="Individual Status">
              <option value="new">New</option>
              <option value="needs_attention">Needs Attention</option>
              <option value="contact_attempted">Contact Attempted</option>
              <option value="contacted">Contacted (Green)</option>
              <option value="engaged">Engaged</option>
              <option value="heating">Heating Up</option>
              <option value="cooling">Going Cold</option>
              <option value="slipping">Slipping</option>
              <option value="stale">Stale</option>
              <option value="silent">Silent</option>
              <option value="waiting">Waiting</option>
              <option value="pending">Pending</option>
              <option value="converted">Converted</option>
              <option value="dropped">Dropped</option>
            </optgroup>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Latency Mode</label>
          <select
            value={latencyMode}
            onChange={(e) => setLatencyMode(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-white transition-all"
          >
            <option value="new">Lead Age (Speed to Lead)</option>
            <option value="active">Since Last Activity (Follow-up)</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
            {latencyMode === 'new' ? 'Lead Age' : 'Time Since Activity'}
          </label>
          <select
            value={filterLatency}
            onChange={(e) => setFilterLatency(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-white transition-all"
          >
            <option value="all">All Time</option>
            <option value="0-24h">0-24h (Fresh)</option>
            <option value="24-48h">24-48h (Catch before cooling)</option>
            <option value="48-72h">48-72h (Rescue slipping)</option>
            <option value="72h+">72h+ (Stale/Re-engage)</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Quick Filters</label>
          <select
            onChange={(e) => {
              const preset = e.target.value;
              if (preset === 'hot_fresh') {
                setFilterHealth('heating');
                setFilterLatency('0-24h');
                setLatencyMode('active');
              } else if (preset === 'new_untouched') {
                setFilterHealth('unprocessed');
                setFilterLatency('all');
                setLatencyMode('new');
              } else if (preset === 'rescue_now') {
                setFilterHealth('at_risk');
                setFilterLatency('48-72h');
                setLatencyMode('active');
              } else if (preset === 'dead_leads') {
                setFilterHealth('neglected');
                setFilterLatency('72h+');
                setLatencyMode('active');
              } else if (preset === 'speed_to_lead') {
                setFilterHealth('unprocessed');
                setFilterLatency('0-24h');
                setLatencyMode('new');
              }
              e.target.value = '';
            }}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm bg-white transition-all"
          >
            <option value="">Select Preset...</option>
            <option value="hot_fresh">Hot & Fresh (Active now)</option>
            <option value="new_untouched">New & Untouched</option>
            <option value="speed_to_lead">Speed to Lead (&lt;24h)</option>
            <option value="rescue_now">Rescue Now (At Risk)</option>
            <option value="dead_leads">Dead Leads (Re-engage)</option>
          </select>
        </div>
      </div>

      {/* Advanced Clinic Filters Row (Only renders if Clinic) */}
      {user?.businessType === 'Clinic' && (
        <div className="grid grid-cols-1 gap-3 pt-3 border-t border-gray-100 bg-blue-50/30 -mx-4 px-4 pb-3">
          <div>
            <label className="block text-xs font-medium text-blue-800 mb-1.5 uppercase tracking-wide">Diagnosis</label>
            <ExcelFilterDropdown inline 
              options={uniqueDiagnosis}
              selectedValues={filterDiagnosis}
              onChange={setFilterDiagnosis}
              placeholder="All Diagnoses"
              label="diagnosis"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-blue-800 mb-1.5 uppercase tracking-wide">Treatment</label>
            <ExcelFilterDropdown inline 
              options={uniqueTreatments}
              selectedValues={filterTreatmentType}
              onChange={setFilterTreatmentType}
              placeholder="All Treatments"
              label="treatment"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-blue-800 mb-1.5 uppercase tracking-wide">Package Name</label>
            <ExcelFilterDropdown inline 
              options={uniquePackages}
              selectedValues={filterPackageName}
              onChange={setFilterPackageName}
              placeholder="All Packages"
              label="package"
            />
          </div>
        </div>
      )}


                      </div>
                      <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-200" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
                        <button type="button" onClick={clearAllFilters} className="min-h-[44px] px-2 text-sm font-bold text-gray-600 underline underline-offset-4 decoration-gray-300">Clear all</button>
                        <button type="button" onClick={() => setFiltersExpanded(false)} className="min-h-[44px] px-5 rounded-xl bg-[#10203A] text-white text-sm font-bold">
                          Show {fmt(serverPagination.totalFiltered || 0)} {(serverPagination.totalFiltered || 0) === 1 ? 'lead' : 'leads'}
                        </button>
                      </div>
                    </aside>
                  </div>
                )}
              </>);
            })()}

            {/* Leads Table */}
            {leadsLoading ? (
              <LoadingSkeleton type="table" />
            ) : filteredLeads.length === 0 ? (
              <div className="bg-white rounded-xl shadow p-12 text-center">
                <Users size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">No Leads Found</h3>
                <p className="text-gray-600 mb-4">
                  {leads.length === 0
                    ? 'Start by adding leads manually or importing from CSV'
                    : 'Try adjusting your filters'}
                </p>
                {leads.length === 0 && (
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={() => setShowCSVImport(true)}
                      className="hs-btn hs-btn-primary"
                    >
                      Import CSV
                    </button>
                    <button
                      onClick={() => setShowAddLead(true)}
                      className="hs-btn hs-btn-primary"
                    >
                      Add Lead
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
              {/* 📱 Phone: select all (the table header with its tick box is hidden on phones) */}
              {paginatedLeads.length > 0 && (() => {
                const allOnPage = paginatedLeads.every(l => selectedLeadIds.includes(l._id));
                return (
                  <div className="md:hidden mb-2 bg-white border border-gray-200 rounded-xl px-3 py-2 space-y-1.5">
                    <label className="flex items-center gap-3 min-h-[40px] cursor-pointer">
                      <input type="checkbox" checked={allOnPage}
                        onChange={(e) => e.target.checked ? selectAllLeads() : deselectAllLeads()}
                        className="w-5 h-5 rounded border-gray-300 accent-[#10203A]" />
                      <span className="text-sm font-bold text-[#10203A]">
                        {selectAllMatching ? `All ${totalMatchingCount} matching leads selected`
                          : selectedLeadIds.length > 0 ? `${selectedLeadIds.length} selected` : `Select all on this page (${paginatedLeads.length})`}
                      </span>
                      {(selectedLeadIds.length > 0 || selectAllMatching) && (
                        <button type="button" onClick={(e) => { e.preventDefault(); deselectAllLeads(); }}
                          className="ml-auto min-h-[36px] px-2 text-sm font-semibold text-gray-600 underline underline-offset-4">Clear</button>
                      )}
                    </label>
                    {allOnPage && !selectAllMatching && totalMatchingCount > paginatedLeads.length && (
                      <button type="button" onClick={selectAllMatchingLeads}
                        className="w-full min-h-[40px] rounded-lg bg-slate-100 text-sm font-bold text-[#10203A]">
                        Select all {totalMatchingCount} leads matching this filter
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* 📱 Phone: one card per lead. The desktop table is hidden under md. */}
              <MobileLeadList
                leads={paginatedLeads}
                selectedIds={selectedLeadIds}
                onToggleSelect={(id) => setSelectedLeadIds(prev =>
                  prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                )}
                onOpen={(lead) => { setSelectedLead(lead); setShowViewLead(true); }}
                onMessage={(lead) => { setSelectedLead(lead); setShowSendMessage(true); }}
                onCalled={(lead) => { pendingCallRef.current = { lead, at: Date.now() }; }}
              />

              <div className="hidden md:block bg-white rounded-xl shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        {/* Fixed: Checkbox */}
                        <th className="px-4 py-3 w-10">
                          <input
                            type="checkbox"
                            checked={paginatedLeads.length > 0 && paginatedLeads.every(l => selectedLeadIds.includes(l._id))}
                            onChange={(e) => e.target.checked ? selectAllLeads() : deselectAllLeads()}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            title="Select all on this page"
                          />
                        </th>
                        
                        {/* Fixed: Lead Name/Phone */}
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-600">Lead and interest</th>
                        
                        {/* ✅ DYNAMIC COLUMNS */}
                        {getVisibleColumns.map(column => (
                          <th key={column.key} className="px-4 py-3 text-left text-xs font-bold text-gray-600 whitespace-nowrap">
                            {column.label}
                          </th>
                        ))}
                        
                        {/* Fixed: Actions */}
                        <th className="px-4 py-3 text-right text-xs font-bold text-gray-600"><span className="sr-only">Actions</span></th>
                      </tr>
                      
                      {/* ✅ NEW: Gmail-style "Select All Matching" Banner */}
                      {selectedLeadIds.length > 0 && 
                      selectedLeadIds.length === paginatedLeads.length && 
                      !selectAllMatching && 
                      totalMatchingCount > paginatedLeads.length && (
                        <tr>
                          <td colSpan={getVisibleColumns.length + 3} className="bg-blue-50 border-b border-blue-100 px-4 py-2">
                            <div className="flex items-center justify-center gap-2 text-sm text-blue-800">
                              <span>All <strong>{paginatedLeads.length}</strong> leads on this page are selected.</span>
                              <button
                                onClick={selectAllMatchingLeads}
                                className="font-medium text-blue-600 hover:text-blue-800 underline"
                              >
                                Select all {totalMatchingCount} leads matching this filter
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                      
                      {/* ✅ NEW: Confirmation when all matching selected */}
                      {selectAllMatching && (
                        <tr>
                          <td colSpan={getVisibleColumns.length + 3} className="bg-green-50 border-b border-green-100 px-4 py-2">
                            <div className="flex items-center justify-center gap-2 text-sm text-green-800">
                              <CheckCircle size={16} className="text-green-600" />
                              <span>All <strong>{totalMatchingCount}</strong> leads matching this filter are selected.</span>
                              <button
                                onClick={deselectAllLeads}
                                className="font-medium text-green-600 hover:text-green-800 underline"
                              >
                                Clear selection
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paginatedLeads.map((lead, index) => (
                        <tr key={lead._id} className="hover:bg-gray-50">
                          {/* Fixed: Checkbox */}
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={selectedLeadIds.includes(lead._id)}
                              onChange={() => toggleLeadSelection(lead._id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          
                          {/* Fixed: Lead Name/Phone/Source - Always visible */}
                          <td className="px-4 py-3">
                            <div className="min-w-0 max-w-[300px]">
                              <p className="font-bold text-[#10203A] truncate cursor-pointer hover:underline underline-offset-2" role="button" tabIndex={0} title="Open this lead"
                                onClick={() => { setSelectedLead(lead); setShowViewLead(true); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedLead(lead); setShowViewLead(true); } }}>
                                {lead.name || formatPhone(lead.phone)}
                                {lead.segment === 'hot' && <span className="ml-1.5 text-xs font-extrabold text-orange-800">Hot</span>}
                                {lead.segment === 'warm' && <span className="ml-1.5 text-xs font-extrabold text-amber-800">Warm</span>}
                                {tierOf(lead) && <span className="ml-1.5 text-[11px] font-bold px-1.5 py-px rounded-md bg-slate-100">{tierOf(lead)}</span>}
                              </p>
                              <p className="text-sm text-gray-600 truncate">{[formatPhone(lead.phone), interestOf(lead)].filter(Boolean).join(' · ')}</p>
                              {/* ✅ Company / student — also shows why a search matched */}
                              {lead.business?.companyName && !['false', 'true'].includes(String(lead.business.companyName).toLowerCase()) && (
                                <p className="text-xs text-purple-700 truncate max-w-[220px]" title={lead.business.companyName}>{lead.business.companyName}</p>
                              )}
                              {lead.school?.studentName && (
                                <p className="text-xs text-blue-700 truncate max-w-[220px]">{lead.school.studentName}</p>
                              )}
                              {!getVisibleColumns.some(c => c.key === 'sourceCampaign' || c.key === 'source') && <p className="text-xs text-gray-600">{lead.source}</p>}
                            </div>
                          </td>
                          
                          {/* ✅ DYNAMIC COLUMNS - Renders based on active view */}
                          {getVisibleColumns.map(column => (
                            <td key={column.key} className="px-4 py-3">
                              {renderColumnValue(lead, column.key)}
                            </td>
                          ))}
                          
                          {/* Fixed: Actions - Always visible */}
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1.5">
                              {isCallerRole && (
                                <button type="button" onClick={() => { setLogCallSecs(undefined); setLogCallLead(lead); }}
                                  className="min-h-[36px] px-3 rounded-lg bg-[#F2B12A] text-[#10203A] text-sm font-bold whitespace-nowrap">Log call</button>
                              )}
                              <button
                                onClick={() => { setSelectedLead(lead); setShowViewLead(true); }}
                                className="p-2 hover:bg-blue-50 rounded"
                                title="View Details"
                              >
                                <Eye size={16} className="text-blue-600" />
                              </button>
                              <button
                                onClick={() => { setSelectedLead(lead); setShowEditLead(true); }}
                                className="p-2 hover:bg-gray-100 rounded"
                                title="Edit Lead"
                              >
                                <Edit2 size={16} className="text-gray-600" />
                              </button>
                              <button
                                onClick={() => { setSelectedLead(lead); setShowSendMessage(true); }}
                                className="p-2 hover:bg-green-50 rounded"
                                title="Send WhatsApp Message"
                              >
                                <MessageSquare size={16} className="text-green-600" />
                              </button>
                              {(user?.role === 'admin' || user?.role === 'owner') && (
                                <button
                                  onClick={() => handleDeleteLead(lead._id, lead.name)}
                                  className="p-2 hover:bg-red-50 rounded"
                                  title="Delete Lead"
                                >
                                  <Trash2 size={16} className="text-red-600" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
              </div>
              {/* ============ PAGER: same on computers and phones ============ */}
              {(() => {
                const total = serverPagination.totalFiltered || 0;
                const from = total === 0 ? 0 : startIndex + 1;
                const to = Math.min(startIndex + paginatedLeads.length, total);
                const pages = Math.max(1, totalPages || 1);
                const nums = (() => {
                  const out = []; const s = Math.max(1, Math.min(currentPage - 2, pages - 4)); const e = Math.min(pages, s + 4);
                  for (let i = s; i <= e; i++) out.push(i); return out;
                })();
                const go = (p) => { setCurrentPage(Math.max(1, Math.min(pages, p))); try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) {} };
                return (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3 bg-white border border-gray-200 rounded-xl px-3 sm:px-4 py-2.5">
                    <p className="text-sm text-gray-600">Showing <span className="font-bold text-[#10203A]">{from.toLocaleString('en-IN')}–{to.toLocaleString('en-IN')}</span> of <span className="font-bold text-[#10203A]">{total.toLocaleString('en-IN')}</span></p>
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <span>Per page</span>
                      <select value={leadsPerPage} onChange={(e) => { setLeadsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                        className="min-h-[38px] px-2 border border-gray-300 rounded-lg bg-white text-sm font-bold text-[#10203A]">
                        {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </label>
                    <div className="flex items-center gap-1.5 w-full sm:w-auto">
                      <button type="button" onClick={() => go(currentPage - 1)} disabled={currentPage <= 1}
                        className="hs-btn hs-btn-secondary flex-1 sm:flex-none min-h-[40px]">Previous</button>
                      <span className="sm:hidden text-sm text-gray-600 px-2 whitespace-nowrap">Page {currentPage} of {pages}</span>
                      <div className="hidden sm:flex items-center gap-1">
                        {nums[0] > 1 && <><button type="button" onClick={() => go(1)} className="min-h-[40px] min-w-[40px] rounded-lg border border-gray-300 text-sm font-bold text-[#10203A]">1</button>{nums[0] > 2 && <span className="px-1 text-gray-500">…</span>}</>}
                        {nums.map(n => (
                          <button key={n} type="button" onClick={() => go(n)} aria-current={n === currentPage ? 'page' : undefined}
                            className={`min-h-[40px] min-w-[40px] rounded-lg border text-sm font-bold ${n === currentPage ? 'bg-[#10203A] border-[#10203A] text-white' : 'border-gray-300 text-[#10203A] hover:bg-gray-50'}`}>{n}</button>
                        ))}
                        {nums[nums.length - 1] < pages && <>{nums[nums.length - 1] < pages - 1 && <span className="px-1 text-gray-500">…</span>}<button type="button" onClick={() => go(pages)} className="min-h-[40px] min-w-[40px] rounded-lg border border-gray-300 text-sm font-bold text-[#10203A]">{pages}</button></>}
                      </div>
                      <button type="button" onClick={() => go(currentPage + 1)} disabled={currentPage >= pages}
                        className="hs-btn hs-btn-secondary flex-1 sm:flex-none min-h-[40px]">Next</button>
                    </div>
                  </div>
                );
              })()}
              </>
            )}
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
        <Suspense fallback={<LazyLoadFallback type="tab" />}>  
        <MessagesTab
          messages={messages}
          messagesMeta={messagesMeta}
          messagesLoading={messagesLoading}
          loadMessages={loadMessages}
          onMessageSent={addSentMessage}
          leads={leads}
          whatsappStatus={whatsappStatus}
          onViewLead={(lead) => { setSelectedLead(lead); setShowViewLead(true); }}
          onEditLead={async (lead) => {
            // ✅ Open Edit Lead modal with the FULL lead, not the short inbox copy
            const full = leads.find(l => l._id === lead._id);
            if (full) { setSelectedLead(full); setShowEditLead(true); return; }
            try {
              const token = localStorage.getItem('token');
              const res = await fetch(`${config.API_URL}/api/leads/${lead._id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              const data = await res.json();
              setSelectedLead(data.success ? data.data : lead);
            } catch (err) {
              console.error('Could not load lead for edit:', err);
              setSelectedLead(lead);
            }
            setShowEditLead(true);
          }}
          onSendTemplate={(lead) => { setSelectedLead(lead); setShowSendMessage(true); }}
          user={user}           // ✅ ADD: For assignment indicator
          templates={[]}        // ✅ ADD: Quick templates (empty for now)
        />
        </Suspense>
      )}


        {/* ✅ ADD THIS ENTIRE SECTION - Reports Tab */}
        {activeTab === 'reports' && (
          <>
            {reportView === 'dashboard' && (
              <Suspense fallback={<LazyLoadFallback type="tab" />}>
              <ReportsDashboard 
                user={user}
                onCreateReport={() => { setSelectedReportConfig(null); setReportView('builder'); }}
                onViewReport={(config) => {
                  setSelectedReportConfig(config);
                  setReportView('viewer');
                }}
              />
              </Suspense>
            )}
            
            {reportView === 'builder' && (
              <Suspense fallback={<LazyLoadFallback type="tab" />}>
              <ReportBuilder
                key={selectedReportConfig?._id || 'new'}
                initialConfig={selectedReportConfig}
                user={user}
                onBack={() => setReportView('dashboard')}
                onPreview={(config) => {
                  setSelectedReportConfig(config);
                  setReportView('viewer');
                }}
                // 👇 FIX: Use our new function to actually hit the database
                onSave={saveReport}
              />
              </Suspense>
            )}
            
            {reportView === 'viewer' && (
              <Suspense fallback={<LazyLoadFallback type="tab" />}>
              <ReportViewer
                reportConfig={selectedReportConfig}
                onBack={() => {
                  setReportView('dashboard');
                  setSelectedReportConfig(null);
                }}
                onEdit={() => setReportView('builder')}
              />
              </Suspense>
            )}
          </>
        )}


        {/* Drips Tab */}
        {activeTab === 'drips' && (
          <Suspense fallback={<LazyLoadFallback type="tab" />}>
          <QuickAutomations user={user} />
          </Suspense>
        )}

        {/* ============================================
              WORKFLOWS TAB
              ============================================ */}
          {activeTab === 'workflows' && (
            <Suspense fallback={<LazyLoadFallback type="tab" />}>
              <WorkflowsTab user={user} />
            </Suspense>
          )}

        {/* Broadcasts Tab */}
        {activeTab === 'broadcasts' && (
          <Suspense fallback={<LazyLoadFallback type="tab" />}>
          <BroadcastDashboard user={user} onNewBroadcast={() => setShowBulkMessage(true)} />
          </Suspense>
        )}

                {activeTab === 'fees' && (
          <Suspense fallback={<LoadingSkeleton type="table" />}>
            <FeesTab
              user={user}
              onViewLead={(id) => {
                const lead = leads.find(x => x._id === id) || { _id: id };
                setSelectedLead(lead);
                setShowViewLead(true);
              }}
            />
          </Suspense>
        )}

        {isLoggedIn && (
          <Suspense fallback={null}>
            <PwaPrompt />
          </Suspense>
        )}

        {activeTab === 'webchats' && (
          <Suspense fallback={<LoadingSkeleton type="table" />}>
            <WebChatsTab
              user={user}
              onViewLead={(id) => {
                const lead = leads.find(x => x._id === id) || { _id: id };
                setSelectedLead(lead);
                setShowViewLead(true);
              }}
            />
          </Suspense>
        )}

                {activeTab === 'orders' && (
          <Suspense fallback={<LoadingSkeleton type="table" />}>
            <OrdersTab
              user={user}
              onViewLead={(id) => {
                const lead = leads.find(x => x._id === id) || { _id: id };
                setSelectedLead(lead);
                setShowViewLead(true);
              }}
            />
          </Suspense>
        )}

        {/* ✅ Contacts Tab - Broadcast Only Contacts */}
        {activeTab === 'appointments' && (
          <Suspense fallback={<LoadingSkeleton type="table" />}>
            <AppointmentsTab
              user={user}
              onViewLead={(l) => {
                // ✅ Works even if the lead isn't on the current Leads page —
                // the View Lead pop-up loads the full lead itself.
                const id = l?._id || l;
                const lead = leads.find(x => x._id === id) || (typeof l === 'object' ? l : { _id: id });
                setSelectedLead(lead);
                setShowViewLead(true);
              }}
            />
          </Suspense>
        )}

        {activeTab === 'contacts' && (
          <Suspense fallback={<LazyLoadFallback type="tab" />}>
            <ContactsTab user={user} />
          </Suspense>
        )}

        {/* ✅ WhatsApp Config Tab - Admin/Owner Only */}
        {activeTab === 'whatsapp-config' && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <MessageSquare size={24} className="text-green-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">WhatsApp Configuration</h1>
                <p className="text-gray-600">Manage your WhatsApp API connection and chatbot settings</p>
              </div>
            </div>
            
            <Suspense fallback={<LazyLoadFallback type="tab" />}>
            <WhatsAppSettings 
              user={user} 
              whatsappStatus={whatsappStatus} 
              onStatusChange={loadWhatsAppStatus} 
            />
            </Suspense>
            <Suspense fallback={<LazyLoadFallback type="tab" />}>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <TemplateLibrary user={user} />
            </div>
            </Suspense>
            <Suspense fallback={<LazyLoadFallback type="tab" />}>
            <ChatbotSettings user={user} />
            </Suspense>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* ============================================
                SUPER ADMIN / ADMIN / OWNER - FULL SETTINGS ACCESS
                dducme@gmail.com = Super Admin with ALL access
                ============================================ */}
            {(user?.email?.toLowerCase() === 'dducme@gmail.com' || 
              user?.role === 'admin' || 
              user?.role === 'owner') && (
              (() => {
                const SECTIONS = [
                  { id: 'general', label: 'General', hint: 'Business hours, response time, login security' },
                  { id: 'team', label: 'Team & assignment', hint: 'Users and who gets which leads' },
                  { id: 'sources', label: 'Lead sources', hint: 'Google Sheets and landing page webhook' },
                  { id: 'ads', label: 'Ads & tracking', hint: 'Conversion API for Meta and Google' },
                  { id: 'store', label: 'Store', hint: 'WhatsApp catalog, order messages, Shopify, WooCommerce' },
                  { id: 'webchat', label: 'Website chat', hint: 'The chat assistant on your website' },
                  { id: 'instagram', label: 'Instagram', hint: 'Instagram DMs in your Messages inbox' },
                  { id: 'ai', label: 'AI apps', hint: 'Claude, ChatGPT and other AI apps connected to Heroshhi' },
                  { id: 'activity', label: 'Activity log', hint: 'Who changed what, and when' }
                ];
                const cur = SECTIONS.find(x => x.id === settingsSection) || SECTIONS[0];
                const box = (children) => <Suspense fallback={<LazyLoadFallback type="tab" />}>{children}</Suspense>;
                return (
                  <div className="lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-6 max-w-7xl">
                    {/* Menu: dropdown on phones, list on computers */}
                    <div className="lg:hidden mb-4">
                      <select value={cur.id} onChange={e => openSettingsSection(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl bg-white text-sm font-medium">
                        {SECTIONS.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
                      </select>
                    </div>
                    <nav className="hidden lg:block">
                      <div className="sticky top-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-2 space-y-0.5">
                        {SECTIONS.map(x => (
                          <button key={x.id} onClick={() => openSettingsSection(x.id)}
                            className={`w-full text-left px-3 py-2.5 rounded-xl text-sm flex items-center gap-2.5 transition-colors ${x.id === cur.id ? 'bg-amber-50 text-[#10203A] font-semibold' : 'text-gray-600 hover:bg-gray-50 hover:text-[#10203A]'}`}>
                            <BrandIcon name={x.id} size={20} active={x.id === cur.id} className="flex-none" />{x.label}
                          </button>
                        ))}
                      </div>
                    </nav>

                    <div className="min-w-0 space-y-6">
                      <div>
                        <h2 className="text-xl font-bold text-[#10203A] flex items-center gap-2"><BrandIcon name={cur.id} size={24} active /> {cur.label}</h2>
                        <p className="text-sm text-gray-500">{cur.hint}</p>
                      </div>

                      {cur.id === 'general' && (<>
                        {box(<BusinessHoursSettings />)}
                        {box(<TwoFactorSettings user={user} onUpdate={() => window.location.reload()} />)}
                      </>)}

                      {cur.id === 'team' && (<>
                        {box(<UserManagement leads={leads} messages={messages} onUsersChange={loadUsers} />)}
                        {box(<div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"><AssignmentRules user={user} /></div>)}
                        {box(<div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"><MessageRouting /></div>)}
                      </>)}

                      {cur.id === 'sources' && (<>
                        {box(<GoogleSheetsSettings onLeadsRefresh={loadLeads} />)}
                        {box(<WebhookSettings />)}
                      </>)}

                      {cur.id === 'ads' && box(<ConversionApiSettings />)}
                      {cur.id === 'store' && box(<StoreSettings />)}
                      {cur.id === 'webchat' && box(<WebChatSettings user={user} />)}
                      {cur.id === 'activity' && box(<AuditLogPanel user={user} users={allUsers} />)}
                      {cur.id === 'ai' && box(<ConnectedAIApps />)}
                      {cur.id === 'instagram' && box(<InstagramSettings />)}
                    </div>
                  </div>
                );
              })()
            )}

            {/* ============================================
                MANAGER/SALES - Profile View Only + Contact Admin
                (Excludes super admin, admin, owner)
                ============================================ */}
            {(user?.email?.toLowerCase() !== 'dducme@gmail.com' && 
              user?.role !== 'admin' && 
              user?.role !== 'owner' &&
              (user?.role === 'manager' || user?.role === 'sales' || user?.role === 'user')) && (
              <>
                {/* Profile Settings Card - VIEW ONLY */}
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-2xl">
                        {(user?.name || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800">My Profile</h2>
                      <p className="text-sm text-gray-600">{user?.email}</p>
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                        user?.role === 'manager' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {user?.role || 'sales'}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Profile Information - Read Only */}
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <User size={18} />
                        Profile Information
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Name</label>
                          <p className="text-gray-900 font-medium">{user?.name || '-'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                          <p className="text-gray-900">{user?.email || '-'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Phone</label>
                          <p className="text-gray-900">{user?.phone || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Role</label>
                          <p className="text-gray-900 capitalize">{user?.role || 'sales'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Contact Admin Card */}
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h3 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                        <Shield size={18} />
                        Need Changes?
                      </h3>
                      <div className="space-y-4 text-sm text-blue-800">
                        <p>
                          To update your profile or reset your password, please contact your administrator.
                        </p>
                        <div className="pt-3 border-t border-blue-200">
                          <p className="font-medium mb-2">Admin can help you with:</p>
                          <ul className="space-y-1 text-blue-700">
                            <li>• Update name or phone number</li>
                            <li>• Reset your password</li>
                            <li>• Change role or permissions</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 🔐 Two-Factor Authentication - Available to ALL users */}
                <Suspense fallback={<LazyLoadFallback type="tab" />}>
                  <TwoFactorSettings user={user} onUpdate={() => window.location.reload()} />
                </Suspense>

                {/* AI apps this team member connected */}
                <Suspense fallback={<LazyLoadFallback type="tab" />}>
                  <ConnectedAIApps />
                </Suspense>

                {/* Limited Access Info Card */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <AlertCircle size={24} className="text-yellow-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-yellow-900">Limited Settings Access</h3>
                      <p className="text-sm text-yellow-700 mt-1">
                        As a {user?.role || 'team member'}, you can view your profile information here. 
                        System settings like WhatsApp API, Business Hours, User Management, and Integrations 
                        are managed by your administrator.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Support Admin Tab - Add after Settings tab */}
        {activeTab === 'support' && (
          <Suspense fallback={<LazyLoadFallback type="tab" />}>
          <SupportAdminPanel 
            apiBaseUrl={config.API_URL}
            currentAgent={user}
          />
          </Suspense>
        )}

        {/* ✅ Platform Admin Tab - Only for Super Admin */}
        {activeTab === 'platform' && (
          <Suspense fallback={<LazyLoadFallback type="tab" />}>
          <PlatformAdminDashboard 
            user={user}
            apiBaseUrl={config.API_URL}
          />
          </Suspense>
        )}

      </ErrorBoundary>
      </main>
      </div>

      {/* Modals */}
      {showAddLead && (
        <Suspense fallback={<LazyLoadFallback type="modal" />}>
        <AddLeadModal
          onClose={() => setShowAddLead(false)}
          onAdd={() => { loadLeads(); setShowAddLead(false); }}
          user={user}  // ✅ ADD THIS LINE HERE
        />
        </Suspense>
      )}

      {showEditLead && selectedLead && (
        <Suspense fallback={<LazyLoadFallback type="modal" />}>
        <EditLeadModal
          lead={selectedLead}
          onClose={() => { setShowEditLead(false); setSelectedLead(null); }}
          onUpdate={() => {
            loadLeads();
            // ✅ If editing from Messages, refresh the inbox too (name, badge)
            if (activeTab === 'messages') loadMessages();
            setShowEditLead(false);
            setSelectedLead(null);
          }}
        />
        </Suspense>
      )}

      {showViewLead && selectedLead && (
        <Suspense fallback={<LazyLoadFallback type="modal" />}>
        <ViewLeadModal
          lead={selectedLead}
          onClose={() => { setShowViewLead(false); setSelectedLead(null); }}
          onEdit={() => { setShowViewLead(false); setShowEditLead(true); }}          // keeps the same lead selected
          onMessage={() => { setShowViewLead(false); setShowSendMessage(true); }}
          onUpdate={async () => {
            await loadLeads();
            // ✅ FIX: Refresh the modal's own lead object with fresh data,
            // not just the background table — otherwise it shows stale data
            try {
              const token = localStorage.getItem('token');
              const res = await fetch(`${config.API_URL}/api/leads/${selectedLead._id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              const data = await res.json();
              if (data.success) setSelectedLead(data.data);
            } catch (err) {
              console.error('Failed to refresh selected lead:', err);
            }
          }}
        />
        </Suspense>
      )}

      {logCallLead && (
        <LogCallSheet lead={logCallLead} callSeconds={logCallSecs}
          onClose={() => { setLogCallLead(null); setLogCallSecs(undefined); }}
          onSaved={() => { loadLeads(); }} />
      )}

      {showSendMessage && selectedLead && (
        <SendMessageModal
          whatsappStatus={whatsappStatus}
          lead={selectedLead}
          onClose={() => { setShowSendMessage(false); setSelectedLead(null); }}
          onSent={() => { loadLeads(); loadMessages(); setShowSendMessage(false); setSelectedLead(null); }}
        />
      )}

      {/* ✅ UPDATED: CSV Import Modal with correct handler */}
      {showCSVImport && (
        <Suspense fallback={<LazyLoadFallback type="modal" />}>
        <CSVImportModal
          isOpen={showCSVImport}
          onClose={() => setShowCSVImport(false)}
          onImport={handleCSVImport}
          user={user}
          users={allUsers}
        />
        </Suspense>
      )}

      {/* ✅ Import Progress Modal */}
      {showImportProgress && importJobId && (
        <Suspense fallback={<LazyLoadFallback type="modal" />}>
          <ImportProgressModal
            isOpen={showImportProgress}
            onClose={() => {
              setShowImportProgress(false);
              setImportJobId(null);
              loadLeads();
            }}
            jobId={importJobId}
            totalLeads={importTotalLeads}
            onComplete={handleImportComplete}
          />
        </Suspense>
      )}

      {showGoogleSheets && (
        <GoogleSheetsModal
          onClose={() => setShowGoogleSheets(false)}
          onConnect={() => { loadSheetsStatus(); setShowGoogleSheets(false); }}
        />
      )}

      {showCreateDrip && (
        <CreateDripModal
          onClose={() => setShowCreateDrip(false)}
          onCreate={() => { loadDrips(); setShowCreateDrip(false); }}
        />
      )}
          {/* ✅ FIXED: Bulk Message Modal */}
          <Suspense fallback={<LazyLoadFallback type="modal" />}>
    <BulkMessageModal
      isOpen={showBulkMessage}
      onClose={() => {
        setShowBulkMessage(false);
        setSelectedLeadIds([]);
      }}
      leads={filteredLeads}
      selectedLeadIds={selectedLeadIds}
      user={user}
      onComplete={(result) => {
        console.log('Bulk send complete:', result);
        loadMessages();
        loadLeads();
      }}
    />
    </Suspense>

    {/* ✅ NEW: Bulk Edit Modal */}
      {showBulkEditModal && (
        <BulkEditModal
          isOpen={showBulkEditModal}
          onClose={() => {
            setShowBulkEditModal(false);
          }}
          selectedLeadIds={selectedLeadIds}
          users={allUsers}
          selectAllMatching={selectAllMatching}
          filterCriteria={getCurrentFilterCriteria()}
          totalCount={selectAllMatching ? totalMatchingCount : selectedLeadIds.length}
          onComplete={() => {
            loadLeads();
            setSelectedLeadIds([]);
            setSelectAllMatching(false);
            setShowBulkEditModal(false);
          }}
        />
      )}

      {/* Bulk Tag Update Modal */}
      {showBulkTagModal && (
        <BulkTagModal
          isOpen={showBulkTagModal}
          onClose={() => {
            setShowBulkTagModal(false);
          }}
          selectedLeadIds={selectedLeadIds}
          selectAllMatching={selectAllMatching}
          filterCriteria={getCurrentFilterCriteria()}
          totalCount={selectAllMatching ? totalMatchingCount : selectedLeadIds.length}
          onComplete={() => {
            loadLeads();
            setSelectedLeadIds([]);
            setSelectAllMatching(false);
            setShowBulkTagModal(false);
          }}
        />
      )}

      {/* ✅ Column Customizer Modal */}
      {showColumnCustomizer && (
        <ColumnCustomizer
          isOpen={showColumnCustomizer}
          onClose={() => {
            setShowColumnCustomizer(false);
            setEditingView(null);
            setIsCreatingNewView(false);
          }}
          currentView={editingView || [...BUILTIN_LAYOUTS, ...tableViews].find(v => v._id === activeViewId)}
          isNewView={isCreatingNewView}
          onSave={async (data) => {
            const viewId = editingView?._id || activeViewId;
            await updateTableView(viewId, data);
          }}
          onCreateNew={createTableView}
        />
      )}

      {/* ✅ ADD THIS - Support Chat Widget */}
      {isLoggedIn && user && (
        <Suspense fallback={null}>
        {/* ✅ Hidden in Messages on every screen — it covered the chat's Send button */}
        <div className={activeTab === 'messages' ? 'hidden' : ''}>
        <SupportChatWidget 
          user={user}
          apiBaseUrl={config.API_URL}
          primaryColor="#10B981"
          teamName="Support Team"
          welcomeMessage="Hi there! 👋 How can we help you today?"
          quickReplies={[
            "How do I connect WhatsApp?",
            "Help with importing leads",
            "Billing question",
            "How can I update my plan?",
            "Need a new feature or functionality?",
            "Report a bug"
          ]}
        />
          </div>
        </Suspense>
      )}

      {/* ============================================ */}
          {/* SESSION TIMEOUT WARNING MODAL */}
          {/* ============================================ */}
          {showTimeoutWarning && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
              <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Session Timeout Warning</h3>
                    <p className="text-sm text-gray-500">Your session is about to expire</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-6">
                  You will be automatically logged out in <strong>2 minutes</strong> due to inactivity. 
                  Click below to stay logged in.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowTimeoutWarning(false);
                      resetInactivityTimer();
                    }}
                    className="hs-btn hs-btn-primary flex-1"
                  >
                    Stay Logged In
                  </button>
                  <button
                    onClick={handleSessionTimeout}
                    className="hs-btn hs-btn-secondary"
                  >
                    Logout Now
                  </button>
                </div>
              </div>
            </div>
          )}
    </div>
  );
}
// plain-dashboard-v1
